import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  ButtonGroup,
  Tooltip,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { Document as PdfDocument, Page as PdfPage, pdfjs } from "react-pdf";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import * as mammoth from "mammoth/mammoth.browser";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import {
  getDocumentSasUrl,
  downloadDocumentProxy,
  getArchiveFileAccess,
  serveHtmlWithSas,
  downloadArchiveFileProxy,
  exportHtmlManualPdf,
} from '../../services/documentService';
import { normalizeDocumentCategoryKey } from "../../utils/documentCategories";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import { SERVER_BASE_URL } from "../../config";

const getModalCategoryLabel = (t, categoryKey) => {
  const labels = {
    manual: t("LABELS.MANUAL") || "Manual",
    boms: t("LABELS.BOM") || "BOM",
    "mechanical-drawings": t("LABELS.MECHANICAL_DRAWING") || "Mechanical Drawing",
    "controls-drawings": t("LABELS.CONTROLS_DRAWINGS") || "Controls Drawings",
    certification: t("LABELS.CERTIFICATION") || "Certification",
    "food-compliance": t("LABELS.FOOD_COMPLIANCE") || "Food Compliance",
    translations: t("LABELS.MANUALS") || "Manuals",
    others: t("LABELS.OTHER") || "Other",
  };

  return labels[categoryKey] || categoryKey;
};

if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

/* ------------------------------------------------------------------ */
/* Keyframes injected once                                            */
/* ------------------------------------------------------------------ */

const SHIMMER_KEYFRAMES = `
  @keyframes shimmerSlide {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }
  @keyframes shimmerPulse {
    0%, 100% { opacity: 0.55; }
    50%       { opacity: 0.9;  }
  }
  @keyframes shimmerFadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0);   }
  }
`;

if (typeof document !== "undefined" && !document.getElementById("dvm-shimmer-styles")) {
  const style = document.createElement("style");
  style.id = "dvm-shimmer-styles";
  style.textContent = SHIMMER_KEYFRAMES;
  document.head.appendChild(style);
}

/* ------------------------------------------------------------------ */
/* Design tokens                                                       */
/* ------------------------------------------------------------------ */

const TOKENS = {
  base: "#F0F2F5",
  highlight: "#FFFFFF",
  stroke: "#E2E6ED",
  radius: "8px",
  shimmerBg: (alpha1 = "rgba(240,242,245,1)", alpha2 = "rgba(255,255,255,0.9)") =>
    `linear-gradient(90deg, ${alpha1} 25%, ${alpha2} 50%, ${alpha1} 75%)`,
};

/* ------------------------------------------------------------------ */
/* Primitive shimmer block                                            */
/* ------------------------------------------------------------------ */

const S = ({
  w = "100%",
  h = 16,
  radius = TOKENS.radius,
  mb = 0,
  mt = 0,
  ml = 0,
  mr = 0,
  delay = 0,
  style = {},
}) => ({
  width: w,
  height: h,
  borderRadius: radius,
  marginBottom: mb,
  marginTop: mt,
  marginLeft: ml,
  marginRight: mr,
  background: TOKENS.shimmerBg(),
  backgroundSize: "200% 100%",
  animation: `shimmerSlide 1.6s ease-in-out infinite, shimmerFadeIn 0.4s ease-out both`,
  animationDelay: `${delay}s, ${delay}s`,
  flexShrink: 0,
  ...style,
});

const ShimmerBlock = (props) => <Box sx={S(props)} />;

/* ------------------------------------------------------------------ */
/* Reusable: Toolbar shimmer (top bar with fake buttons)              */
/* ------------------------------------------------------------------ */

const ToolbarShimmer = ({ withTabs = false }) => (
  <Box
    sx={{
      width: "100%",
      background: "#FFFFFF",
      borderBottom: `1px solid ${TOKENS.stroke}`,
      px: 2,
      py: 1.25,
      display: "flex",
      alignItems: "center",
      gap: 1,
      flexShrink: 0,
      animation: "shimmerFadeIn 0.3s ease-out both",
    }}
  >
    {/* Left cluster */}
    {[40, 40, 40, 52, 40].map((w, i) => (
      <ShimmerBlock key={i} w={w} h={30} radius="6px" delay={i * 0.04} />
    ))}
    <Box sx={{ flex: 1 }} />
    {/* Right cluster */}
    {[80, 60, 36].map((w, i) => (
      <ShimmerBlock key={`r${i}`} w={w} h={30} radius="6px" delay={0.2 + i * 0.04} />
    ))}
  </Box>
);

/* ------------------------------------------------------------------ */
/* Reusable: Page chrome (fake page with lines)                       */
/* ------------------------------------------------------------------ */

const PageLinesShimmer = ({ lines = 14, titleHeight = 28, delay = 0 }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
    <ShimmerBlock w="55%" h={titleHeight} delay={delay} mb={8} />
    {Array.from({ length: lines }).map((_, i) => (
      <ShimmerBlock
        key={i}
        w={i % 7 === 6 ? "42%" : i % 5 === 4 ? "78%" : `${82 + (i % 4) * 4}%`}
        h={14}
        delay={delay + i * 0.025}
      />
    ))}
  </Box>
);

/* ------------------------------------------------------------------ */
/* DocumentShimmer — the upgraded main component                     */
/* ------------------------------------------------------------------ */

const DocumentShimmer = ({ contentType, documentName }) => {
  const getShimmerType = () => {
    if (!contentType && !documentName) return "default";
    const ext = documentName?.split(".").pop()?.toLowerCase();

    if (contentType?.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "tiff", "ico"].includes(ext))
      return "image";
    if (contentType === "application/pdf" || ext === "pdf")
      return "pdf";
    if (contentType?.startsWith("video/") || ["mp4", "mov", "avi", "mkv", "webm", "mpeg", "mpg", "m4v", "3gp", "flv", "wmv"].includes(ext))
      return "video";
    if (contentType?.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a", "aac", "flac", "opus", "weba", "oga"].includes(ext))
      return "audio";
    if (
      contentType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      contentType === "application/msword" || ["doc", "docx"].includes(ext)
    ) return "document";
    if (
      contentType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      contentType === "application/vnd.ms-excel" ||
      contentType === "text/csv" ||
      ["xls", "xlsx", "csv"].includes(ext)
    ) return "spreadsheet";
    if (
      contentType === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
      contentType === "application/vnd.ms-powerpoint" || ["ppt", "pptx"].includes(ext)
    ) return "presentation";
    if (contentType === "text/html" || ["html", "htm"].includes(ext))
      return "html";
    if (contentType === "text/plain" || contentType === "application/json" || contentType === "application/xml" || contentType === "text/markdown"
      || ["txt", "json", "xml", "md", "log", "yaml", "yml", "csv", "ini", "cfg"].includes(ext))
      return "text";
    return "default";
  };

  const type = getShimmerType();

  /* Shared wrapper -------------------------------------------------- */
  const Wrap = ({ children, noPad = false }) => (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F7F9FC",
        animation: "shimmerFadeIn 0.35s ease-out both",
      }}
    >
      {children}
    </Box>
  );

  /* Loading badge ---------------------------------------------------- */
  const LoadingBadge = ({ label = "Loading…" }) => (
    <Box
      sx={{
        position: "absolute",
        bottom: 20,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 1,
        borderRadius: 100,
        background: "rgba(255,255,255,0.92)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.10)",
        backdropFilter: "blur(6px)",
        border: `1px solid ${TOKENS.stroke}`,
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "linear-gradient(135deg,#4F8EF7,#7B5EA7)",
          animation: "shimmerPulse 1.2s ease-in-out infinite",
        }}
      />
      <Typography variant="caption" sx={{ fontWeight: 600, color: "#555", letterSpacing: "0.03em" }}>
        {label}
      </Typography>
    </Box>
  );

  /* ════════════════════════════ PDF ════════════════════════════════ */
  if (type === "pdf") return (
    <Wrap>
      <ToolbarShimmer />
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden", position: "relative" }}>
        {/* Sidebar thumbnails */}
        <Box
          sx={{
            width: 88,
            background: "#EAEDF2",
            borderRight: `1px solid ${TOKENS.stroke}`,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            p: 1.5,
            flexShrink: 0,
          }}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                ...S({ w: "100%", h: 112, radius: "5px", delay: i * 0.06 }),
                border: i === 0 ? "2px solid #4F8EF7" : `1px solid ${TOKENS.stroke}`,
              }}
            />
          ))}
        </Box>

        {/* Main page area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            overflowY: "auto",
            background: "#E8EBF0",
            p: 3,
            gap: 3,
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {[0, 1].map((pageIdx) => (
            <Box
              key={pageIdx}
              sx={{
                width: "min(680px, 100%)",
                background: "#FFFFFF",
                borderRadius: "6px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
                p: "40px 56px",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                animation: `shimmerFadeIn 0.4s ease-out ${pageIdx * 0.15}s both`,
              }}
            >
              {/* Page header */}
              <ShimmerBlock w="65%" h={22} delay={pageIdx * 0.1} mb={16} />
              <PageLinesShimmer lines={12} titleHeight={0} delay={pageIdx * 0.1 + 0.1} />
              {/* Section break */}
              <Box sx={{ ...S({ w: "100%", h: 1, delay: 0.5 }), my: 2, background: TOKENS.stroke }} />
              <PageLinesShimmer lines={8} titleHeight={18} delay={pageIdx * 0.1 + 0.35} />
            </Box>
          ))}
        </Box>
      </Box>
      <LoadingBadge label="Loading PDF…" />
    </Wrap>
  );

  /* ════════════════════════════ IMAGE ══════════════════════════════ */
  if (type === "image") return (
    <Wrap>
      <Box
        sx={{
          flex: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          background: "#EAECF0",
          p: 3,
        }}
      >
        {/* Fake image placeholder */}
        <Box
          sx={{
            width: "min(640px, 92%)",
            aspectRatio: "4/3",
            borderRadius: "10px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box sx={S({ w: "100%", h: "100%", radius: "10px" })} />
          {/* Fake image inner structure */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              opacity: 0.35,
            }}
          >
            {/* Mountain silhouette shape */}
            <Box sx={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(255,255,255,0.6)" }} />
            <Box sx={{ width: 120, height: 8, borderRadius: 4, background: "rgba(255,255,255,0.5)" }} />
            <Box sx={{ width: 80, height: 8, borderRadius: 4, background: "rgba(255,255,255,0.4)" }} />
          </Box>
        </Box>

        {/* Caption area */}
        <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
          {[64, 96, 72, 64].map((w, i) => (
            <ShimmerBlock key={i} w={w} h={22} radius="100px" delay={i * 0.08} />
          ))}
        </Box>

        <LoadingBadge label="Loading Image…" />
      </Box>
    </Wrap>
  );

  /* ════════════════════════════ VIDEO ══════════════════════════════ */
  if (type === "video") return (
    <Wrap>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#1A1D23",
          gap: 0,
          position: "relative",
        }}
      >
        {/* Video canvas */}
        <Box
          sx={{
            width: "min(800px, 95%)",
            aspectRatio: "16/9",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #23262E 0%, #2D3040 100%)",
            boxShadow: "0 8px 48px rgba(0,0,0,0.5)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Subtle scan-line shimmer */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(90deg, rgba(255,255,255,0) 25%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0) 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmerSlide 2s ease-in-out infinite",
            }}
          />
          {/* Play button */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                border: "2px solid rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "shimmerPulse 2s ease-in-out infinite",
              }}
            >
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderTop: "14px solid transparent",
                  borderBottom: "14px solid transparent",
                  borderLeft: "22px solid rgba(255,255,255,0.25)",
                  ml: "4px",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Control bar */}
        <Box
          sx={{
            width: "min(800px, 95%)",
            mt: 0,
            background: "#23262E",
            borderRadius: "0 0 10px 10px",
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box sx={{ ...S({ w: 32, h: 32, radius: "50%" }), opacity: 0.4 }} />
          <Box sx={{ flex: 1, ...S({ h: 4, radius: "100px" }), opacity: 0.3 }} />
          <Box sx={{ ...S({ w: 48, h: 14, radius: 4 }), opacity: 0.3 }} />
          {[28, 28, 28].map((w, i) => (
            <Box key={i} sx={{ ...S({ w, h: 28, radius: "50%" }), opacity: 0.3, delay: i * 0.05 }} />
          ))}
        </Box>

        <LoadingBadge label="Loading Video…" />
      </Box>
    </Wrap>
  );

  /* ════════════════════════════ SPREADSHEET ════════════════════════ */
  if (type === "spreadsheet") return (
    <Wrap>
      {/* Ribbon */}
      <Box
        sx={{
          background: "#1D6F42",
          px: 2,
          py: 1,
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {[48, 48, 60, 48, 56, 48].map((w, i) => (
          <Box key={i} sx={{ ...S({ w, h: 26, radius: "4px", delay: i * 0.04 }), opacity: 0.3 }} />
        ))}
        <Box sx={{ flex: 1 }} />
        <Box sx={{ ...S({ w: 36, h: 26, radius: "4px" }), opacity: 0.3 }} />
      </Box>

      {/* Formula bar */}
      <Box
        sx={{
          background: "#FFFFFF",
          borderBottom: `1px solid ${TOKENS.stroke}`,
          px: 2,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <ShimmerBlock w={72} h={26} radius="4px" />
        <Box sx={{ width: 1, height: 24, background: TOKENS.stroke }} />
        <ShimmerBlock w="70%" h={26} radius="4px" delay={0.1} />
      </Box>

      {/* Sheet grid */}
      <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {/* Column headers */}
        <Box
          sx={{
            display: "flex",
            background: "#F3F4F6",
            borderBottom: `1px solid ${TOKENS.stroke}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ width: 52, height: 28, borderRight: `1px solid ${TOKENS.stroke}`, flexShrink: 0 }} />
          {["14%", "12%", "18%", "10%", "14%", "12%", "10%", "10%"].map((w, i) => (
            <Box
              key={i}
              sx={{
                width: w,
                height: 28,
                borderRight: `1px solid ${TOKENS.stroke}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShimmerBlock w="50%" h={12} radius="3px" delay={i * 0.03} />
            </Box>
          ))}
        </Box>

        {/* Rows */}
        {Array.from({ length: 16 }).map((_, row) => (
          <Box
            key={row}
            sx={{
              display: "flex",
              borderBottom: `1px solid ${TOKENS.stroke}`,
              background: row % 2 === 0 ? "#FFFFFF" : "#FAFBFC",
              flexShrink: 0,
              animation: `shimmerFadeIn 0.3s ease-out ${row * 0.02}s both`,
            }}
          >
            {/* Row number */}
            <Box
              sx={{
                width: 52,
                height: 28,
                borderRight: `1px solid ${TOKENS.stroke}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ShimmerBlock w={24} h={11} radius="3px" delay={row * 0.025} />
            </Box>
            {["14%", "12%", "18%", "10%", "14%", "12%", "10%", "10%"].map((w, col) => {
              const hasFill = Math.random() > 0.28;
              return (
                <Box
                  key={col}
                  sx={{
                    width: w,
                    height: 28,
                    borderRight: `1px solid ${TOKENS.stroke}`,
                    display: "flex",
                    alignItems: "center",
                    px: 1,
                    flexShrink: 0,
                  }}
                >
                  {hasFill && (
                    <ShimmerBlock
                      w={`${48 + ((row * 7 + col * 13) % 40)}%`}
                      h={11}
                      radius="3px"
                      delay={(row * 8 + col) * 0.008}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Sheet tabs */}
      <Box
        sx={{
          background: "#F0F2F5",
          borderTop: `1px solid ${TOKENS.stroke}`,
          px: 2,
          py: 0.75,
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {["Sheet 1", "Sheet 2", "Sheet 3"].map((_, i) => (
          <Box
            key={i}
            sx={{
              ...S({ w: 72, h: 26, radius: "4px 4px 0 0", delay: i * 0.06 }),
              background: i === 0
                ? `linear-gradient(90deg, #FFFFFF 25%, rgba(255,255,255,0.9) 50%, #FFFFFF 75%)`
                : undefined,
              backgroundSize: "200% 100%",
            }}
          />
        ))}
        <Box sx={{ ...S({ w: 28, h: 26, radius: "50%" }), opacity: 0.5, ml: 1 }} />
      </Box>
    </Wrap>
  );

  /* ════════════════════════════ WORD / DOCUMENT ════════════════════ */
  if (type === "document") return (
    <Wrap>
      {/* Word-style ribbon */}
      <Box
        sx={{
          background: "#2B579A",
          px: 2,
          py: 0.75,
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {[52, 48, 64, 52, 56, 48, 48].map((w, i) => (
          <Box key={i} sx={{ ...S({ w, h: 26, radius: "4px", delay: i * 0.04 }), opacity: 0.28 }} />
        ))}
        <Box sx={{ flex: 1 }} />
        {[28, 28].map((w, i) => (
          <Box key={i} sx={{ ...S({ w, h: 26, radius: "50%" }), opacity: 0.28 }} />
        ))}
      </Box>

      {/* Formatting toolbar */}
      <Box
        sx={{
          background: "#F3F5FA",
          borderBottom: `1px solid ${TOKENS.stroke}`,
          px: 2,
          py: 0.75,
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {[120, 1, 32, 32, 32, 32, 1, 32, 32, 32, 1, 100, 1, 32, 32, 32].map((w, i) =>
          w === 1
            ? <Box key={i} sx={{ width: 1, height: 20, background: TOKENS.stroke, mx: 0.5 }} />
            : <ShimmerBlock key={i} w={w} h={26} radius="4px" delay={i * 0.02} />
        )}
      </Box>

      {/* Page canvas */}
      <Box
        sx={{
          flex: 1,
          background: "#E8EBF0",
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          p: "28px 16px",
          gap: 3,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {[0, 1].map((pageIdx) => (
          <Box
            key={pageIdx}
            sx={{
              width: "min(680px, 100%)",
              background: "#FFFFFF",
              borderRadius: "3px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
              p: "56px 72px",
              display: "flex",
              flexDirection: "column",
              gap: 1.75,
              animation: `shimmerFadeIn 0.4s ease-out ${pageIdx * 0.18}s both`,
            }}
          >
            {pageIdx === 0 && (
              <>
                {/* Title */}
                <ShimmerBlock w="70%" h={28} delay={0} mb={4} />
                {/* Author / date line */}
                <Box sx={{ display: "flex", gap: 2, mb: 12 }}>
                  <ShimmerBlock w={120} h={13} delay={0.05} />
                  <ShimmerBlock w={80} h={13} delay={0.08} />
                </Box>
              </>
            )}
            <PageLinesShimmer lines={pageIdx === 0 ? 14 : 18} titleHeight={22} delay={pageIdx * 0.18 + 0.1} />

            {pageIdx === 0 && (
              <>
                <Box sx={{ ...S({ w: "100%", h: 1 }), background: TOKENS.stroke, my: 2 }} />
                <Box sx={{ display: "flex", gap: 2 }}>
                  {[0, 1].map((col) => (
                    <Box key={col} sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.25 }}>
                      <ShimmerBlock w="60%" h={16} delay={0.4 + col * 0.05} />
                      {Array.from({ length: 5 }).map((_, i) => (
                        <ShimmerBlock key={i} w={`${75 + i * 5}%`} h={12} delay={0.45 + col * 0.05 + i * 0.03} />
                      ))}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        ))}
      </Box>

      {/* Status bar */}
      <Box
        sx={{
          background: "#2B579A",
          px: 2,
          py: 0.5,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {[80, 1, 60, 1, 100].map((w, i) =>
          w === 1
            ? <Box key={i} sx={{ width: 1, height: 14, background: "rgba(255,255,255,0.2)" }} />
            : <Box key={i} sx={{ ...S({ w, h: 12, radius: "3px", delay: i * 0.04 }), opacity: 0.25 }} />
        )}
        <Box sx={{ flex: 1 }} />
        {[80, 60].map((w, i) => (
          <Box key={i} sx={{ ...S({ w, h: 12, radius: "3px" }), opacity: 0.25 }} />
        ))}
      </Box>
    </Wrap>
  );

  /* ════════════════════════════ PRESENTATION ═══════════════════════ */
  if (type === "presentation") return (
    <Wrap>
      {/* PowerPoint ribbon */}
      <Box
        sx={{
          background: "#C43E1C",
          px: 2,
          py: 0.75,
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {[52, 48, 64, 52, 56, 48, 48].map((w, i) => (
          <Box key={i} sx={{ ...S({ w, h: 26, radius: "4px", delay: i * 0.04 }), opacity: 0.28 }} />
        ))}
      </Box>

      {/* Main area */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Slide strip */}
        <Box
          sx={{
            width: 156,
            background: "#F0F2F5",
            borderRight: `1px solid ${TOKENS.stroke}`,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            p: 1.5,
            overflowY: "auto",
            flexShrink: 0,
          }}
        >
          {Array.from({ length: 9 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                width: "100%",
                aspectRatio: "16/9",
                borderRadius: "5px",
                border: i === 0 ? "2px solid #C43E1C" : `1px solid ${TOKENS.stroke}`,
                overflow: "hidden",
                background: "#FFFFFF",
                position: "relative",
                flexShrink: 0,
                animation: `shimmerFadeIn 0.3s ease-out ${i * 0.05}s both`,
              }}
            >
              <Box sx={{ ...S({ w: "100%", h: "100%", radius: "3px" }), opacity: 0.6 }} />
              <Box sx={{
                position: "absolute", bottom: 4, left: 6, right: 6,
                display: "flex", flexDirection: "column", gap: 0.5
              }}>
                <Box sx={{ ...S({ w: "70%", h: 4, radius: "2px" }), opacity: 0.5 }} />
                <Box sx={{ ...S({ w: "50%", h: 3, radius: "2px" }), opacity: 0.35 }} />
              </Box>
            </Box>
          ))}
        </Box>

        {/* Main slide view */}
        <Box
          sx={{
            flex: 1,
            background: "#D4D7DE",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: "min(720px, 96%)",
              aspectRatio: "16/9",
              background: "#FFFFFF",
              borderRadius: "6px",
              boxShadow: "0 4px 32px rgba(0,0,0,0.18)",
              p: "40px 56px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              gap: 2,
            }}
          >
            {/* Slide accent */}
            <Box sx={{ width: "100%", height: 6, borderRadius: "3px", ...S({ h: 6, radius: "3px" }), background: "linear-gradient(90deg,#C43E1C,#E07050)", backgroundSize: "200% 100%", animation: "shimmerSlide 1.6s ease-in-out infinite", mb: 2 }} />
            <ShimmerBlock w="72%" h={32} delay={0} />
            <ShimmerBlock w="88%" h={20} delay={0.08} />
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: "flex", gap: 2 }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    height: 80,
                    borderRadius: "6px",
                    border: `1px solid ${TOKENS.stroke}`,
                    p: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <ShimmerBlock w={32} h={32} radius="6px" delay={0.2 + i * 0.06} />
                  <ShimmerBlock w="80%" h={10} delay={0.26 + i * 0.06} />
                  <ShimmerBlock w="60%" h={9} delay={0.32 + i * 0.06} />
                </Box>
              ))}
            </Box>
          </Box>
          <LoadingBadge label="Loading Presentation…" />
        </Box>
      </Box>

      {/* Presenter toolbar */}
      <Box
        sx={{
          background: "#1E1E1E",
          px: 2,
          py: 1,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {[32, 32, 1, 80, 1, 32, 32].map((w, i) =>
          w === 1
            ? <Box key={i} sx={{ width: 1, height: 18, background: "rgba(255,255,255,0.15)" }} />
            : <Box key={i} sx={{ ...S({ w, h: 28, radius: w === 32 ? "50%" : "4px", delay: i * 0.04 }), opacity: 0.22 }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Box sx={{ ...S({ w: 96, h: 28, radius: "4px" }), opacity: 0.22 }} />
      </Box>
    </Wrap>
  );

  /* ════════════════════════════ HTML / BROWSER ═════════════════════ */
  if (type === "html") return (
    <Wrap>
      {/* Browser chrome */}
      <Box
        sx={{
          background: "#2D2D2D",
          px: 1.5,
          pt: 1,
          pb: 0,
          flexShrink: 0,
        }}
      >
        {/* Traffic lights + tabs */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          {["#FF5F57", "#FEBC2E", "#28C840"].map((c, i) => (
            <Box key={i} sx={{ width: 12, height: 12, borderRadius: "50%", background: c, opacity: 0.7 }} />
          ))}
          <Box sx={{ flex: 1, mx: 1 }}>
            <Box
              sx={{
                ...S({ w: "55%", h: 28, radius: "100px" }),
                ml: "auto",
                mr: "auto",
                opacity: 0.35,
              }}
            />
          </Box>
          {[28, 28, 28].map((w, i) => (
            <Box key={i} sx={{ ...S({ w, h: 28, radius: "50%" }), opacity: 0.2 }} />
          ))}
        </Box>
        {/* Browser tabs */}
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {[160, 130, 110].map((w, i) => (
            <Box
              key={i}
              sx={{
                ...S({ w, h: 32, radius: "6px 6px 0 0", delay: i * 0.05 }),
                opacity: i === 0 ? 0.6 : 0.25,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Page content area */}
      <Box
        sx={{
          flex: 1,
          background: "#FAFBFC",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Fake nav bar */}
        <Box
          sx={{
            height: 56,
            background: "#FFFFFF",
            borderBottom: `1px solid ${TOKENS.stroke}`,
            px: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <ShimmerBlock w={100} h={28} radius="5px" />
          <Box sx={{ flex: 1 }} />
          {[72, 72, 72, 80].map((w, i) => (
            <ShimmerBlock key={i} w={w} h={20} radius="4px" delay={i * 0.04} />
          ))}
        </Box>

        {/* Hero area */}
        <Box
          sx={{
            height: 180,
            background: "linear-gradient(90deg, #F0F2F5 25%, #F7F9FC 50%, #F0F2F5 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmerSlide 2s ease-in-out infinite",
            borderBottom: `1px solid ${TOKENS.stroke}`,
            px: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <ShimmerBlock w="45%" h={32} delay={0.1} />
          <ShimmerBlock w="62%" h={18} delay={0.15} />
          <ShimmerBlock w="30%" h={16} delay={0.2} />
          <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
            <ShimmerBlock w={110} h={36} radius="100px" delay={0.25} />
            <ShimmerBlock w={90} h={36} radius="100px" delay={0.3} />
          </Box>
        </Box>

        {/* Content grid */}
        <Box sx={{ p: 3, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, overflow: "hidden" }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                borderRadius: "8px",
                border: `1px solid ${TOKENS.stroke}`,
                overflow: "hidden",
                background: "#FFFFFF",
                animation: `shimmerFadeIn 0.35s ease-out ${i * 0.06}s both`,
              }}
            >
              <ShimmerBlock w="100%" h={100} radius="0" />
              <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                <ShimmerBlock w="75%" h={14} delay={i * 0.04} />
                <ShimmerBlock w="90%" h={11} delay={i * 0.04 + 0.04} />
                <ShimmerBlock w="60%" h={11} delay={i * 0.04 + 0.08} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <LoadingBadge label="Loading HTML…" />
    </Wrap>
  );

  /* ════════════════════════════ PLAIN TEXT ═════════════════════════ */
  if (type === "text") return (
    <Wrap>
      {/* Text editor toolbar */}
      <Box
        sx={{
          background: "#FFFFFF",
          borderBottom: `1px solid ${TOKENS.stroke}`,
          px: 2,
          py: 1,
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <ShimmerBlock w={56} h={22} radius="4px" />
        <Box sx={{ width: 1, height: 18, background: TOKENS.stroke, mx: 0.5 }} />
        {[36, 36, 36, 36].map((w, i) => (
          <ShimmerBlock key={i} w={w} h={22} radius="4px" delay={i * 0.04} />
        ))}
        <Box sx={{ flex: 1 }} />
        <ShimmerBlock w={100} h={22} radius="100px" />
      </Box>

      {/* Editor content */}
      <Box
        sx={{
          flex: 1,
          background: "#FAFAFA",
          p: "24px 32px",
          fontFamily: "'Courier New', monospace",
          overflow: "hidden",
          display: "flex",
          gap: 3,
        }}
      >
        {/* Line numbers */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.75,
            flexShrink: 0,
            borderRight: `1px solid ${TOKENS.stroke}`,
            pr: 2,
          }}
        >
          {Array.from({ length: 20 }).map((_, i) => (
            <ShimmerBlock key={i} w={24} h={12} radius="3px" delay={i * 0.02} />
          ))}
        </Box>

        {/* Text content */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.75 }}>
          {Array.from({ length: 20 }).map((_, i) => {
            const isEmpty = i % 6 === 5;
            const indent = i % 8 >= 5 ? 24 : 0;
            return (
              <Box key={i} sx={{ pl: `${indent}px` }}>
                {!isEmpty && (
                  <ShimmerBlock
                    w={isEmpty ? 0 : `${55 + ((i * 13) % 38)}%`}
                    h={12}
                    radius="3px"
                    delay={i * 0.018}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Status bar */}
      <Box
        sx={{
          background: "#F0F2F5",
          borderTop: `1px solid ${TOKENS.stroke}`,
          px: 2,
          py: 0.5,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {[60, 80, 100, 80].map((w, i) => (
          <ShimmerBlock key={i} w={w} h={11} radius="3px" delay={i * 0.04} />
        ))}
      </Box>
    </Wrap>
  );

  /* ════════════════════════════ AUDIO ═════════════════════════════ */
  if (type === "audio") return (
    <Wrap>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          background: "linear-gradient(160deg, #1A1033 0%, #2D1B4E 50%, #1A1033 100%)",
          position: "relative",
          p: 4,
        }}
      >
        {/* Animated vinyl / disc shimmer */}
        <Box sx={{ position: "relative", width: 200, height: 200 }}>
          {/* Outer ring */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: "conic-gradient(from 0deg, rgba(102,126,234,0.3), rgba(118,75,162,0.5), rgba(102,126,234,0.3))",
              animation: "shimmerPulse 2s ease-in-out infinite",
            }}
          />
          {/* Album art placeholder */}
          <Box
            sx={{
              position: "absolute",
              inset: 16,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "3.5rem",
              boxShadow: "0 0 40px rgba(102,126,234,0.4)",
            }}
          >
            🎵
          </Box>
          {/* Center dot */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "#1A1033",
              border: "3px solid rgba(255,255,255,0.15)",
            }}
          />
        </Box>

        {/* Track info shimmers */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, width: "min(320px,90%)" }}>
          <ShimmerBlock w="70%" h={18} style={{ background: "linear-gradient(90deg,rgba(255,255,255,0.15) 25%,rgba(255,255,255,0.3) 50%,rgba(255,255,255,0.15) 75%)", backgroundSize: "200% 100%" }} />
          <ShimmerBlock w="45%" h={13} style={{ background: "linear-gradient(90deg,rgba(255,255,255,0.08) 25%,rgba(255,255,255,0.18) 50%,rgba(255,255,255,0.08) 75%)", backgroundSize: "200% 100%" }} delay={0.07} />
        </Box>

        {/* Waveform bars */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "3px", height: 40 }}>
          {Array.from({ length: 32 }).map((_, i) => {
            const h = 8 + Math.abs(Math.sin(i * 0.7)) * 28;
            return (
              <Box
                key={i}
                sx={{
                  width: 3,
                  height: `${h}px`,
                  borderRadius: "2px",
                  background: "rgba(255,255,255,0.25)",
                  animation: `shimmerPulse ${1.2 + (i % 4) * 0.15}s ease-in-out infinite`,
                  animationDelay: `${i * 0.04}s`,
                  flexShrink: 0,
                }}
              />
            );
          })}
        </Box>

        {/* Fake player controls */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "min(360px,90%)" }}>
          {/* Progress bar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ ...S({ w: 36, h: 12, radius: "3px" }), opacity: 0.3 }} />
            <Box sx={{ flex: 1, height: 4, borderRadius: "2px", background: "rgba(255,255,255,0.15)", overflow: "hidden" }}>
              <Box sx={{ width: "35%", height: "100%", background: "rgba(102,126,234,0.7)", borderRadius: "2px" }} />
            </Box>
            <Box sx={{ ...S({ w: 36, h: 12, radius: "3px" }), opacity: 0.3 }} />
          </Box>
          {/* Buttons row */}
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
            {[28, 28, 48, 28, 28].map((s, i) => (
              <Box key={i} sx={{ ...S({ w: s, h: s, radius: "50%" }), opacity: i === 2 ? 0.5 : 0.25 }} />
            ))}
          </Box>
        </Box>

        <LoadingBadge label="Loading Audio…" />
      </Box>
    </Wrap>
  );

  /* ════════════════════════════ DEFAULT ════════════════════════════ */
  return (
    <Wrap>
      <ToolbarShimmer />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#F0F2F5",
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: "min(560px, 90%)",
            background: "#FFFFFF",
            borderRadius: "10px",
            boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "14px",
              ...S({ w: 64, h: 64, radius: "14px" }),
            }}
          />
          <ShimmerBlock w="55%" h={20} />
          <ShimmerBlock w="75%" h={13} delay={0.05} />
          <ShimmerBlock w="60%" h={13} delay={0.10} />
          <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
            <ShimmerBlock w={100} h={36} radius="100px" delay={0.15} />
            <ShimmerBlock w={80} h={36} radius="100px" delay={0.20} />
          </Box>
        </Box>
        <LoadingBadge label="Loading…" />
      </Box>
    </Wrap>
  );
};

/* ------------------------------------------------------------------ */
/* Helper Functions                                                   */
/* ------------------------------------------------------------------ */

const DEFAULT_ATTACHMENT = { type: "none", value: "" };

const normalizeAttachment = (document) => {
  if (!document) return DEFAULT_ATTACHMENT;

  const value =
    document.path ||
    document.attachment ||
    document.url ||
    (document.name ? `Documents/${document.name}` : "");

  if (value) {
    return { type: "file", value };
  }

  if (document.url) {
    return { type: "url", value: document.url };
  }

  return DEFAULT_ATTACHMENT;
};

/* ------------------------------------------------------------------ */
/* File type helpers                                                  */
/* ------------------------------------------------------------------ */

const MIME_MAP = {
  // Documents
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // Text
  txt: "text/plain",
  csv: "text/csv",
  json: "application/json",
  xml: "application/xml",
  html: "text/html",
  htm: "text/html",
  md: "text/markdown",
  // Images
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  bmp: "image/bmp",
  tiff: "image/tiff",
  ico: "image/x-icon",
  // Video — browser-native formats
  mp4: "video/mp4",
  webm: "video/webm",
  ogv: "video/ogg",
  // Video — non-native (needs info card + download)
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  mkv: "video/x-matroska",
  mpeg: "video/mpeg",
  mpg: "video/mpeg",
  m4v: "video/x-m4v",
  "3gp": "video/3gpp",
  flv: "video/x-flv",
  wmv: "video/x-ms-wmv",
  // Audio
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  m4a: "audio/mp4",
  aac: "audio/aac",
  flac: "audio/flac",
  weba: "audio/webm",
  opus: "audio/opus",
  // Archives
  zip: "application/zip",
  rar: "application/vnd.rar",
  tar: "application/x-tar",
  gz: "application/gzip",
  "7z": "application/x-7z-compressed",
  // Binary
  exe: "application/octet-stream",
  bin: "application/octet-stream",
  iso: "application/octet-stream",
  dmg: "application/octet-stream",
};

// Browser can natively play these video MIME types
/**
 * What the player's CC menu calls each caption track.
 *
 * Only ever one track is attached — the caption file that sits beside the video in the same
 * language folder — so this is a display label, not a language picker. Falls back to the
 * upper-cased code for any language added to storage before it is added here.
 */
const CAPTION_LANGUAGE_LABELS = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
};

const NATIVE_VIDEO_TYPES = new Set([
  "video/mp4", "video/webm", "video/ogg", "video/quicktime",
]);

// Browser can natively play these audio MIME types
const NATIVE_AUDIO_TYPES = new Set([
  "audio/mpeg", "audio/wav", "audio/ogg", "audio/mp4",
  "audio/aac", "audio/webm", "audio/opus", "audio/flac",
]);

const ARCHIVE_TYPES = new Set([
  "application/zip", "application/vnd.rar", "application/x-tar",
  "application/gzip", "application/x-7z-compressed",
]);

const BINARY_TYPES = new Set(["application/octet-stream"]);

const VIDEO_EXTENSIONS = new Set([
  "mp4", "webm", "ogv", "mov", "avi", "mkv",
  "mpeg", "mpg", "m4v", "3gp", "flv", "wmv"
]);

const resolveType = (contentType, documentName, sasUrl, filePath) => {
  const src = documentName || filePath || sasUrl || "";
  const ext = src.split("?")[0].split(".").pop().toLowerCase();

  // Extension ALWAYS wins for video — never trust wrong content_type from DB
  if (VIDEO_EXTENSIONS.has(ext)) {
    return MIME_MAP[ext];
  }

  if (contentType) return contentType;
  return MIME_MAP[ext] || null;
};

const ZoomedIframe = ({ src, title, contentType, documentName, zoom, onLoad, loading }) => {
  const containerRef = useRef(null);
  const lastBaseSrcRef = useRef(null);
  const safeZoom = Number.isFinite(Number(zoom)) ? Number(zoom) : 100;
  const frameScale = safeZoom / 100;

  const handleLoad = () => {
    const currentBaseSrc = (src || "").split("#")[0];
    if (containerRef.current && lastBaseSrcRef.current !== currentBaseSrc) {
      containerRef.current.scrollTop = 0;
      containerRef.current.scrollLeft = 0;
    }
    lastBaseSrcRef.current = currentBaseSrc;
    onLoad?.();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        border: "1px solid #e0e0e0",
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          opacity: loading ? 1 : 0,
          pointerEvents: loading ? "auto" : "none",
          transition: "opacity 0.3s ease",
        }}
      >
        <DocumentShimmer contentType={contentType} documentName={documentName} />
      </Box>
      <Box
        sx={{
          transform: `scale(${frameScale})`,
          transformOrigin: "top left",
          width: safeZoom > 100 ? `${(100 / safeZoom) * 100}%` : "100%",
          height: safeZoom > 100 ? `${(100 / safeZoom) * 100}%` : "100%",
          minHeight: "100%",
          position: "relative",
        }}
      >
        <iframe
          src={src}
          title={title}
          onLoad={handleLoad}
          style={{
            width: "100%",
            height: "100%",
            minHeight: "60vh",
            border: "none",
            visibility: loading ? "hidden" : "visible",
            opacity: loading ? 0 : 1,
            transition: "opacity 0.3s ease",
          }}
        />
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Download-only info card (archives, binaries, unsupported video)   */
/* ------------------------------------------------------------------ */

const DownloadOnlyCard = ({ sasUrl, label, icon, description, ext }) => (
  <Box
    sx={{
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#F7F9FC",
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        p: 5,
        background: "#FFFFFF",
        borderRadius: "14px",
        boxShadow: "0 2px 20px rgba(0,0,0,0.07)",
        border: "1px solid #E8EBF0",
        maxWidth: 420,
        textAlign: "center",
      }}
    >
      {/* File icon badge */}
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "16px",
          background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "2rem",
          border: "1px solid #C7D2FE",
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#1E293B" }}>
        {label}
      </Typography>
      {ext && (
        <Chip
          label={`.${ext.toUpperCase()}`}
          size="small"
          sx={{ background: "#EEF2FF", color: "#4F46E5", fontWeight: 700, fontSize: "0.7rem" }}
        />
      )}
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {description}
      </Typography>
      {sasUrl && (
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => {
            const a = document.createElement("a");
            a.href = sasUrl;
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }}
          sx={{ mt: 1, borderRadius: "100px", px: 3 }}
        >
          Download File
        </Button>
      )}
    </Box>
  </Box>
);

const PdfPreviewWithLoader = ({ src, documentName, zoom, documentId = null, isArchiveFile = false, filePath = null }) => {
  const [pdfSource, setPdfSource] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(true);
  const [viewerLoading, setViewerLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [viewerWidth, setViewerWidth] = useState(0);
  const viewerRef = useRef(null);
  const safeZoom = Number.isFinite(Number(zoom)) ? Math.round(Number(zoom)) : 100;
  const pdfZoom = safeZoom / 100;
  const pageWidth = Math.max(viewerWidth || 760, 320);

  useEffect(() => {
    const element = viewerRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      // Use offsetWidth (includes scrollbar) so that scrollbar appearance/
      // disappearance does not change the measured value.  Subtract a fixed
      // padding (48px content padding + 17px worst-case scrollbar) to keep
      // pages from overflowing.
      const nextWidth = Math.max((element.offsetWidth || 0) - 65, 320);
      setViewerWidth((prev) => {
        // Only commit a state update when the difference is meaningful
        // (> 20px) to avoid oscillation between scrollbar on/off states.
        if (prev && Math.abs(nextWidth - prev) <= 20) return prev;
        return nextWidth;
      });
    };

    updateWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => updateWidth());
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    let mounted = true;
    let objectUrl = null;

    const preparePdfSource = async () => {
      setSourceLoading(true);
      setViewerLoading(true);
      setPdfSource(null);
      setPdfError(null);
      setNumPages(0);
      const canUseDirectSource = !documentId && !(isArchiveFile && filePath);

      try {
        let blob = null;

        if (documentId) {
          blob = await downloadDocumentProxy(documentId);
        } else if (isArchiveFile && filePath) {
          blob = await downloadArchiveFileProxy(filePath);
        }

        if (blob?.type === "application/json") {
          throw new Error("Proxy returned JSON instead of PDF blob.");
        }

        if (blob && blob.size > 0) {
          objectUrl = window.URL.createObjectURL(blob);
          if (mounted) setPdfSource(objectUrl);
        } else if (mounted && canUseDirectSource) {
          setPdfSource(src);
        } else if (mounted) {
          setPdfError("Failed to load PDF preview source.");
        }
      } catch (err) {
        console.error("Failed to prepare PDF source:", err);
        if (mounted && canUseDirectSource) {
          setPdfSource(src);
        } else if (mounted) {
          setPdfError("Failed to load PDF preview source.");
        }
      } finally {
        if (mounted) setSourceLoading(false);
      }
    };

    preparePdfSource();

    return () => {
      mounted = false;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, documentId, isArchiveFile, filePath]);

  const showLoader = sourceLoading || viewerLoading;

  return (
    <Box
      ref={viewerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: showLoader ? "hidden" : "auto",
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        background: "#EEF1F5",
        scrollbarWidth: "thin",
        scrollbarColor: "#9AA7B8 #DDE3EC",
        "&::-webkit-scrollbar": {
          width: 10,
          height: 10,
        },
        "&::-webkit-scrollbar-track": {
          background: "#DDE3EC",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#9AA7B8",
          borderRadius: "10px",
          border: "2px solid #DDE3EC",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#7D8DA3",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          opacity: showLoader ? 1 : 0,
          pointerEvents: showLoader ? "auto" : "none",
          transition: "opacity 0.3s ease",
        }}
      >
        <DocumentShimmer contentType="application/pdf" documentName={documentName} />
      </Box>

      {pdfError ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {pdfError}
        </Alert>
      ) : pdfSource ? (
        <PdfDocument
          file={pdfSource}
          loading={null}
          onLoadSuccess={({ numPages: totalPages }) => {
            setNumPages(totalPages || 0);
            setViewerLoading(false);
          }}
          onLoadError={(err) => {
            console.error("Failed to render PDF preview:", err);
            setPdfError("Failed to render PDF preview.");
            setViewerLoading(false);
          }}
        >
          <Box
            sx={{
              minHeight: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2.5,
              py: 2.5,
              px: 2,
            }}
          >
            {Array.from({ length: numPages }, (_, idx) => (
              <Box
                key={`pdf-page-${idx + 1}`}
                sx={{
                  zoom: pdfZoom,
                  background: "#FFFFFF",
                  borderRadius: "4px",
                  boxShadow: "0 2px 16px rgba(0,0,0,0.16)",
                  overflow: "hidden",
                }}
              >
                <PdfPage
                  pageNumber={idx + 1}
                  width={pageWidth}
                  scale={1}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  loading={null}
                />
              </Box>
            ))}
          </Box>
        </PdfDocument>
      ) : null}
    </Box>
  );
};

const ExcelPreviewWithLoader = ({ src, documentName, zoom, documentId = null, isArchiveFile = false, filePath = null }) => {
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [sourceLoading, setSourceLoading] = useState(true);
  const [excelError, setExcelError] = useState(null);
  const viewerRef = useRef(null);
  const safeZoom = Number.isFinite(Number(zoom)) ? Math.round(Number(zoom)) : 100;
  const excelZoom = safeZoom / 100;

  useEffect(() => {
    let mounted = true;

    const loadExcel = async () => {
      setSourceLoading(true);
      setExcelError(null);
      setSheets([]);
      setActiveSheet(0);

      try {
        let blob = null;

        if (documentId) {
          blob = await downloadDocumentProxy(documentId);
        } else if (isArchiveFile && filePath) {
          blob = await downloadArchiveFileProxy(filePath);
        } else if (src) {
          const resp = await fetch(src);
          if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
          blob = await resp.blob();
        }

        if (!blob || blob.size === 0) {
          throw new Error("Empty or missing file.");
        }

        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array", cellStyles: true, cellText: true });

        if (!mounted) return;

        // Extract images and styles from the xlsx zip
        let sheetImages = {};
        let xlStyles = { fonts: [], fills: [], borders: [], cellXfs: [] };
        let sheetCellStyleIndices = {}; // { sheetIndex: { "A1": styleIdx, ... } }
        let themeColors = [];
        try {
          const zip = await JSZip.loadAsync(arrayBuffer);

          // --- Parse theme colors from xl/theme/theme1.xml ---
          try {
            const themeFile = zip.file("xl/theme/theme1.xml");
            if (themeFile) {
              const themeXml = await themeFile.async("text");
              // Theme index mapping used by XLSX colors:
              // 0=lt1, 1=dk1, 2=lt2, 3=dk2, 4..9=accent1..6, 10=hlink, 11=folHlink.
              // This is not the same as the raw clrScheme node order in theme1.xml.
              const themeOrder = ["lt1", "dk1", "lt2", "dk2", "accent1", "accent2", "accent3", "accent4", "accent5", "accent6", "hlink", "folHlink"];
              for (const name of themeOrder) {
                const tagRegex = new RegExp(`<a:${name}>([\\s\\S]*?)<\\/a:${name}>`);
                const match = themeXml.match(tagRegex);
                if (match) {
                  const sysClrMatch = match[1].match(/lastClr="([A-Fa-f0-9]{6})"/);
                  const srgbClrMatch = match[1].match(/val="([A-Fa-f0-9]{6})"/);
                  themeColors.push(sysClrMatch ? sysClrMatch[1] : (srgbClrMatch ? srgbClrMatch[1] : "000000"));
                } else {
                  themeColors.push("000000");
                }
              }
            }
          } catch (themeErr) {
            console.warn("Could not parse theme:", themeErr);
          }

          // Helper to resolve a color element to a hex string
          const resolveColor = (colorXml) => {
            if (!colorXml) return null;
            // rgb attribute (ARGB format - first 2 chars are alpha)
            const rgbMatch = colorXml.match(/rgb="([A-Fa-f0-9]{6,8})"/);
            if (rgbMatch) {
              const val = rgbMatch[1];
              return `#${val.length === 8 ? val.slice(2) : val}`;
            }
            // theme attribute with optional tint
            const themeMatch = colorXml.match(/theme="(\d+)"/);
            if (themeMatch && themeColors.length > 0) {
              const themeIdx = parseInt(themeMatch[1], 10);
              let baseColor = themeColors[themeIdx] || "000000";
              const tintMatch = colorXml.match(/tint="([^"]+)"/);
              if (tintMatch) {
                const tint = parseFloat(tintMatch[1]);
                // Apply tint: positive lightens, negative darkens
                let r = parseInt(baseColor.slice(0, 2), 16);
                let g = parseInt(baseColor.slice(2, 4), 16);
                let b = parseInt(baseColor.slice(4, 6), 16);
                if (tint > 0) {
                  r = Math.round(r + (255 - r) * tint);
                  g = Math.round(g + (255 - g) * tint);
                  b = Math.round(b + (255 - b) * tint);
                } else {
                  r = Math.round(r * (1 + tint));
                  g = Math.round(g * (1 + tint));
                  b = Math.round(b * (1 + tint));
                }
                return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
              }
              return `#${baseColor}`;
            }
            // indexed color (basic palette)
            const indexedMatch = colorXml.match(/indexed="(\d+)"/);
            if (indexedMatch) {
              const idx = parseInt(indexedMatch[1], 10);
              const indexedPalette = [
                "000000","FFFFFF","FF0000","00FF00","0000FF","FFFF00","FF00FF","00FFFF",
                "000000","FFFFFF","FF0000","00FF00","0000FF","FFFF00","FF00FF","00FFFF",
                "800000","008000","000080","808000","800080","008080","C0C0C0","808080",
                "9999FF","993366","FFFFCC","CCFFFF","660066","FF8080","0066CC","CCCCFF",
                "000080","FF00FF","FFFF00","00FFFF","800080","800000","008080","0000FF",
                "00CCFF","CCFFFF","CCFFCC","FFFF99","99CCFF","FF99CC","CC99FF","FFCC99",
                "3366FF","33CCCC","99CC00","FFCC00","FF9900","FF6600","666699","969696",
                "003366","339966","003300","333300","993300","993366","333399","333333",
              ];
              if (idx < indexedPalette.length) return `#${indexedPalette[idx]}`;
            }
            return null;
          };

          // --- Parse xl/styles.xml ---
          try {
            const stylesFile = zip.file("xl/styles.xml");
            if (stylesFile) {
              const stylesXml = await stylesFile.async("text");

              // Parse fonts
              const fontsBlock = stylesXml.match(/<fonts[^>]*>([\s\S]*?)<\/fonts>/);
              if (fontsBlock) {
                const fontRegex = /<font>([\s\S]*?)<\/font>/g;
                let fm;
                while ((fm = fontRegex.exec(fontsBlock[1])) !== null) {
                  const fontXml = fm[1];
                  const bold = /<b\s*\/>|<b\b[^>]*\/>/.test(fontXml) || /<b>/.test(fontXml);
                  const italic = /<i\s*\/>|<i\b[^>]*\/>/.test(fontXml) || /<i>/.test(fontXml);
                  const underline = /<u\s*\/>|<u\b[^>]*\/>/.test(fontXml) || /<u>/.test(fontXml);
                  const sizeMatch = fontXml.match(/<sz\s+val="([^"]+)"/);
                  const fontSize = sizeMatch ? parseFloat(sizeMatch[1]) : null;
                  const colorTag = fontXml.match(/<color\b[^\/]*\/>/)?.[0] || fontXml.match(/<color\b[^>]*>[^<]*<\/color>/)?.[0] || null;
                  const color = resolveColor(colorTag);
                  xlStyles.fonts.push({ bold, italic, underline, fontSize, color });
                }
              }

              // Parse fills
              const fillsBlock = stylesXml.match(/<fills[^>]*>([\s\S]*?)<\/fills>/);
              if (fillsBlock) {
                const fillRegex = /<fill>([\s\S]*?)<\/fill>/g;
                let flm;
                while ((flm = fillRegex.exec(fillsBlock[1])) !== null) {
                  const fillXml = flm[1];
                  const patternType = fillXml.match(/patternType="([^"]+)"/)?.[1];
                  let bgColor = null;
                  if (patternType && patternType !== "none") {
                    // fgColor is the actual fill color for solid fills
                    const fgColorTag = fillXml.match(/<fgColor\b[^\/]*\/>/)?.[0] || null;
                    const bgColorTag = fillXml.match(/<bgColor\b[^\/]*\/>/)?.[0] || null;
                    bgColor = resolveColor(fgColorTag) || resolveColor(bgColorTag);
                  }
                  xlStyles.fills.push({ bgColor });
                }
              }

              // Parse borders
              const bordersBlock = stylesXml.match(/<borders[^>]*>([\s\S]*?)<\/borders>/);
              if (bordersBlock) {
                const borderRegex = /<border(?:\s[^>]*)?>(([\s\S]*?))<\/border>/g;
                let bm;
                while ((bm = borderRegex.exec(bordersBlock[1])) !== null) {
                  const borderXml = bm[1];
                  const parseSide = (side) => {
                    const sideRegex = new RegExp(`<${side}\\s+style="([^"]+)"[^>]*(?:\\/>|>([\\s\\S]*?)<\\/${side}>)`);
                    const m = borderXml.match(sideRegex);
                    if (m) {
                      const colorTag = m[2] ? (m[2].match(/<color\b[^\/]*\/>/)?.[0] || null) : null;
                      return { style: m[1], color: resolveColor(colorTag) || "#000000" };
                    }
                    return null;
                  };
                  xlStyles.borders.push({
                    left: parseSide("left"),
                    right: parseSide("right"),
                    top: parseSide("top"),
                    bottom: parseSide("bottom"),
                  });
                }
              }

              // Parse cellXfs (cell format records)
              const cellXfsBlock = stylesXml.match(/<cellXfs[^>]*>([\s\S]*?)<\/cellXfs>/);
              if (cellXfsBlock) {
                const xfRegex = /<xf\b([^>]*?)(?:\/>|>([\s\S]*?)<\/xf>)/g;
                let xfm;
                while ((xfm = xfRegex.exec(cellXfsBlock[1])) !== null) {
                  const attrs = xfm[1];
                  const inner = xfm[2] || "";
                  const fontId = parseInt(attrs.match(/fontId="(\d+)"/)?.[1] || "0", 10);
                  const fillId = parseInt(attrs.match(/fillId="(\d+)"/)?.[1] || "0", 10);
                  const borderId = parseInt(attrs.match(/borderId="(\d+)"/)?.[1] || "0", 10);
                  // Parse alignment
                  const alignMatch = inner.match(/<alignment\b([^\/]*?)\/>/);
                  let halign = null, valign = null, wrapText = false;
                  if (alignMatch) {
                    halign = alignMatch[1].match(/horizontal="([^"]+)"/)?.[1] || null;
                    valign = alignMatch[1].match(/vertical="([^"]+)"/)?.[1] || null;
                    wrapText = /wrapText="1"/.test(alignMatch[1]);
                  }
                  xlStyles.cellXfs.push({ fontId, fillId, borderId, halign, valign, wrapText });
                }
              }
            }
          } catch (styleErr) {
            console.warn("Could not parse styles.xml:", styleErr);
          }

          // --- Parse cell style indices from each sheet XML ---
          for (let si = 0; si < workbook.SheetNames.length; si++) {
            const sheetIdx = si + 1;
            const sheetFile = zip.file(`xl/worksheets/sheet${sheetIdx}.xml`);
            if (!sheetFile) continue;
            const sheetXml = await sheetFile.async("text");
            const cellStyleMap = {};
            const cellRegex = /<c\s+r="([A-Z]+\d+)"[^>]*?(?:\/>|>[\s\S]*?<\/c>)/g;
            let cm;
            while ((cm = cellRegex.exec(sheetXml)) !== null) {
              const ref = cm[1];
              const sMatch = cm[0].match(/\bs="(\d+)"/);
              if (sMatch) {
                cellStyleMap[ref] = parseInt(sMatch[1], 10);
              }
            }
            // Also parse row heights
            sheetCellStyleIndices[si] = cellStyleMap;
          }

          // Load all media files as data URLs
          const mediaFiles = {};
          const mediaFolder = zip.folder("xl/media");
          if (mediaFolder) {
            const mediaEntries = [];
            mediaFolder.forEach((relPath, file) => { mediaEntries.push({ relPath, file }); });
            for (const { relPath, file } of mediaEntries) {
              const ext = relPath.split(".").pop().toLowerCase();
              const mimeMap = { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", bmp: "image/bmp", svg: "image/svg+xml", webp: "image/webp", emf: "image/x-emf", wmf: "image/x-wmf" };
              const mime = mimeMap[ext] || "image/png";
              const base64 = await file.async("base64");
              mediaFiles[`../media/${relPath}`] = `data:${mime};base64,${base64}`;
            }
          }

          // Parse drawing relationships and drawings for each sheet
          for (let si = 0; si < workbook.SheetNames.length; si++) {
            const sheetIdx = si + 1;
            const relsPath = `xl/worksheets/_rels/sheet${sheetIdx}.xml.rels`;
            const relsFile = zip.file(relsPath);
            if (!relsFile) continue;

            const relsXml = await relsFile.async("text");
            const drawingMatch = relsXml.match(/Target="[^"]*drawings\/(drawing\d+\.xml)"/);
            if (!drawingMatch) continue;

            const drawingName = drawingMatch[1];
            const drawingFile = zip.file(`xl/drawings/${drawingName}`);
            if (!drawingFile) continue;

            // Parse drawing rels for image references
            const drawingRelsFile = zip.file(`xl/drawings/_rels/${drawingName}.rels`);
            const imageRels = {};
            if (drawingRelsFile) {
              const drawRelsXml = await drawingRelsFile.async("text");
              const relRegex = /Id="(rId\d+)"[^>]*Target="([^"]+)"/g;
              let rm;
              while ((rm = relRegex.exec(drawRelsXml)) !== null) {
                imageRels[rm[1]] = mediaFiles[rm[2]] || null;
              }
            }

            // Parse drawing XML for anchors
            const drawXml = await drawingFile.async("text");
            const images = [];
            const anchorRegex = /<xdr:twoCellAnchor[^>]*>[\s\S]*?<\/xdr:twoCellAnchor>/g;
            let am;
            while ((am = anchorRegex.exec(drawXml)) !== null) {
              const anchor = am[0];
              const fromCol = anchor.match(/<xdr:from>[\s\S]*?<xdr:col>(\d+)<\/xdr:col>/)?.[1];
              const fromRow = anchor.match(/<xdr:from>[\s\S]*?<xdr:row>(\d+)<\/xdr:row>/)?.[1];
              const toCol = anchor.match(/<xdr:to>[\s\S]*?<xdr:col>(\d+)<\/xdr:col>/)?.[1];
              const toRow = anchor.match(/<xdr:to>[\s\S]*?<xdr:row>(\d+)<\/xdr:row>/)?.[1];
              const embedId = anchor.match(/r:embed="(rId\d+)"/)?.[1];

              if (fromCol != null && fromRow != null && embedId && imageRels[embedId]) {
                images.push({
                  src: imageRels[embedId],
                  fromRow: parseInt(fromRow, 10),
                  fromCol: parseInt(fromCol, 10),
                  toRow: toRow != null ? parseInt(toRow, 10) : parseInt(fromRow, 10) + 3,
                  toCol: toCol != null ? parseInt(toCol, 10) : parseInt(fromCol, 10) + 2,
                });
              }
            }
            if (images.length > 0) {
              sheetImages[si] = images;
            }
          }
        } catch (imgErr) {
          console.warn("Could not extract images from xlsx:", imgErr);
        }

        const parsed = workbook.SheetNames.map((name, si) => {
          const ws = workbook.Sheets[name];
          const ref = ws["!ref"] || "A1";
          const range = XLSX.utils.decode_range(ref);
          const rowCount = range.e.r + 1;
          const colCount = range.e.c + 1;
          const cellStyleMap = sheetCellStyleIndices[si] || {};

          // Build cell grid with formatted text and style info from XML-parsed styles
          const cells = [];
          for (let r = 0; r < rowCount; r++) {
            const row = [];
            for (let c = 0; c < colCount; c++) {
              const addr = XLSX.utils.encode_cell({ r, c });
              const cell = ws[addr];
              if (cell) {
                const styleIdx = cellStyleMap[addr];
                const xf = (styleIdx != null && xlStyles.cellXfs[styleIdx]) ? xlStyles.cellXfs[styleIdx] : null;
                const font = xf ? xlStyles.fonts[xf.fontId] : null;
                const fill = xf ? xlStyles.fills[xf.fillId] : null;
                const border = xf ? xlStyles.borders[xf.borderId] : null;
                row.push({
                  v: cell.w != null ? cell.w : (cell.v != null ? String(cell.v) : ""),
                  bold: !!font?.bold,
                  italic: !!font?.italic,
                  underline: !!font?.underline,
                  fontSize: font?.fontSize || null,
                  color: font?.color || null,
                  bgColor: fill?.bgColor || null,
                  halign: xf?.halign || null,
                  valign: xf?.valign || null,
                  wrapText: !!xf?.wrapText,
                  borderLeft: border?.left || null,
                  borderRight: border?.right || null,
                  borderTop: border?.top || null,
                  borderBottom: border?.bottom || null,
                });
              } else {
                row.push({ v: "" });
              }
            }
            cells.push(row);
          }

          // Column widths
          const colWidths = (ws["!cols"] || []).map((col) => {
            if (!col) return null;
            if (col.wpx) return col.wpx;
            if (col.wch) return Math.round(col.wch * 7.5);
            return null;
          });

          const merges = (ws["!merges"] || []).map((m) => ({
            r: m.s.r, c: m.s.c,
            rowSpan: m.e.r - m.s.r + 1,
            colSpan: m.e.c - m.s.c + 1,
          }));

          return { name, cells, colCount, rowCount, merges, colWidths, images: sheetImages[si] || [] };
        });

        if (mounted) setSheets(parsed);
      } catch (err) {
        console.error("Failed to load Excel:", err);
        if (mounted) setExcelError("Failed to load spreadsheet preview.");
      } finally {
        if (mounted) setSourceLoading(false);
      }
    };

    loadExcel();
    return () => { mounted = false; };
  }, [src, documentId, isArchiveFile, filePath]);

  const currentSheet = sheets[activeSheet];

  const isCellMerged = useCallback((rowIdx, colIdx) => {
    if (!currentSheet?.merges) return null;
    for (const m of currentSheet.merges) {
      if (rowIdx === m.r && colIdx === m.c) return { rowSpan: m.rowSpan, colSpan: m.colSpan };
      if (rowIdx >= m.r && rowIdx < m.r + m.rowSpan && colIdx >= m.c && colIdx < m.c + m.colSpan) return "skip";
    }
    return null;
  }, [currentSheet]);

  return (
    <Box
      ref={viewerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        background: "#F7F9FC",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 3,
          opacity: sourceLoading ? 1 : 0,
          pointerEvents: sourceLoading ? "auto" : "none",
          transition: "opacity 0.3s ease",
        }}
      >
        <DocumentShimmer contentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" documentName={documentName} />
      </Box>

      {excelError ? (
        <Alert severity="error" sx={{ m: 2 }}>{excelError}</Alert>
      ) : sheets.length > 0 ? (
        <>
          {/* Sheet tabs */}
          {sheets.length > 1 && (
            <Box
              sx={{
                display: "flex",
                gap: 0,
                borderBottom: "2px solid #E2E6ED",
                background: "#FFFFFF",
                flexShrink: 0,
                overflowX: "auto",
                px: 1,
              }}
            >
              {sheets.map((s, idx) => (
                <Box
                  key={s.name}
                  onClick={() => setActiveSheet(idx)}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    fontWeight: idx === activeSheet ? 700 : 500,
                    color: idx === activeSheet ? "#1565C0" : "#555",
                    borderBottom: idx === activeSheet ? "2px solid #1565C0" : "2px solid transparent",
                    whiteSpace: "nowrap",
                    transition: "all 0.15s ease",
                    "&:hover": { background: "#F0F4FF" },
                  }}
                >
                  {s.name}
                </Box>
              ))}
            </Box>
          )}

          {/* Table */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#9AA7B8 #DDE3EC",
              "&::-webkit-scrollbar": { width: 10, height: 10 },
              "&::-webkit-scrollbar-track": { background: "#DDE3EC", borderRadius: "10px" },
              "&::-webkit-scrollbar-thumb": { background: "#9AA7B8", borderRadius: "10px", border: "2px solid #DDE3EC" },
              "&::-webkit-scrollbar-thumb:hover": { background: "#7D8DA3" },
            }}
          >
            <table
              style={{
                borderCollapse: "collapse",
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                fontSize: `${13 * excelZoom}px`,
              }}
            >
              <colgroup>
                <col style={{ width: 36 * excelZoom, minWidth: 36 * excelZoom }} />
                {Array.from({ length: currentSheet?.colCount || 0 }, (_, ci) => {
                  const w = currentSheet?.colWidths?.[ci];
                  return <col key={ci} style={{ width: w ? w * excelZoom : undefined, minWidth: 60 * excelZoom }} />;
                })}
              </colgroup>
              <thead>
                <tr>
                  <th
                    style={{
                      background: "#E8EBF0",
                      border: "1px solid #D0D5DD",
                      padding: `${6 * excelZoom}px ${4 * excelZoom}px`,
                      color: "#555",
                      fontSize: `${11 * excelZoom}px`,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                    }}
                  />
                  {Array.from({ length: currentSheet?.colCount || 0 }, (_, ci) => (
                    <th
                      key={ci}
                      style={{
                        background: "#E8EBF0",
                        border: "1px solid #D0D5DD",
                        padding: `${6 * excelZoom}px ${8 * excelZoom}px`,
                        fontWeight: 600,
                        color: "#444",
                        fontSize: `${11 * excelZoom}px`,
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        textAlign: "center",
                      }}
                    >
                      {ci < 26 ? String.fromCharCode(65 + ci) : String.fromCharCode(65 + Math.floor(ci / 26) - 1) + String.fromCharCode(65 + (ci % 26))}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: currentSheet?.rowCount || 0 }, (_, ri) => (
                  <tr key={ri}>
                    <td
                      style={{
                        background: "#E8EBF0",
                        border: "1px solid #D0D5DD",
                        padding: `${4 * excelZoom}px ${6 * excelZoom}px`,
                        fontWeight: 600,
                        color: "#555",
                        fontSize: `${11 * excelZoom}px`,
                        textAlign: "center",
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                      }}
                    >
                      {ri + 1}
                    </td>
                    {Array.from({ length: currentSheet?.colCount || 0 }, (_, ci) => {
                      const merge = isCellMerged(ri, ci);
                      if (merge === "skip") return null;
                      const cell = currentSheet?.cells?.[ri]?.[ci] || { v: "" };
                      const cellImage = (currentSheet?.images || []).find(
                        (img) => img.fromRow === ri && img.fromCol === ci
                      );

                      // Build border styles
                      const borderStyleMap = { thin: "1px solid", medium: "2px solid", thick: "3px solid", dashed: "1px dashed", dotted: "1px dotted", double: "3px double", hair: "1px solid" };
                      const makeBorder = (b) => b ? `${borderStyleMap[b.style] || "1px solid"} ${b.color || "#000000"}` : "1px solid #D0D5DD";

                      return (
                        <td
                          key={ci}
                          rowSpan={merge?.rowSpan || (cellImage ? Math.max(1, (cellImage.toRow - cellImage.fromRow) || 1) : undefined)}
                          colSpan={merge?.colSpan || (cellImage ? Math.max(1, (cellImage.toCol - cellImage.fromCol) || 1) : undefined)}
                          style={{
                            borderLeft: makeBorder(cell.borderLeft),
                            borderRight: makeBorder(cell.borderRight),
                            borderTop: makeBorder(cell.borderTop),
                            borderBottom: makeBorder(cell.borderBottom),
                            padding: `${5 * excelZoom}px ${8 * excelZoom}px`,
                            background: cell.bgColor || "#FFFFFF",
                            whiteSpace: cell.wrapText ? "normal" : "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontWeight: cell.bold ? 700 : 400,
                            fontStyle: cell.italic ? "italic" : "normal",
                            textDecoration: cell.underline ? "underline" : "none",
                            color: cell.color || "inherit",
                            fontSize: cell.fontSize ? `${cell.fontSize * excelZoom}px` : undefined,
                            textAlign: cell.halign || "left",
                            verticalAlign: cell.valign === "center" ? "middle" : (cell.valign || "bottom"),
                            position: cellImage ? "relative" : undefined,
                          }}
                        >
                          {cellImage && (
                            <img
                              src={cellImage.src}
                              alt=""
                              style={{
                                maxWidth: "100%",
                                maxHeight: `${(cellImage.toRow - cellImage.fromRow) * 28 * excelZoom}px`,
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          )}
                          {!cellImage && cell.v}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </>
      ) : null}
    </Box>
  );
};

const DocxPreviewWithLoader = ({ src, documentName, zoom, documentId = null, isArchiveFile = false, filePath = null }) => {
  const [docxHtml, setDocxHtml] = useState("");
  const [sourceLoading, setSourceLoading] = useState(true);
  const [docxError, setDocxError] = useState(null);
  const safeZoom = Number.isFinite(Number(zoom)) ? Math.round(Number(zoom)) : 100;
  const docxZoom = safeZoom / 100;

  useEffect(() => {
    let mounted = true;

    const loadDocx = async () => {
      setSourceLoading(true);
      setDocxError(null);
      setDocxHtml("");

      try {
        let blob = null;

        if (documentId) {
          blob = await downloadDocumentProxy(documentId);
        } else if (isArchiveFile && filePath) {
          blob = await downloadArchiveFileProxy(filePath);
        } else if (src) {
          const resp = await fetch(src);
          if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
          blob = await resp.blob();
        }

        if (!blob || blob.size === 0) {
          throw new Error("Empty or missing DOCX file.");
        }

        if (blob.type === "application/json") {
          const payload = JSON.parse(await blob.text());
          throw new Error(payload?.message || "Failed to load DOCX file.");
        }

        const arrayBuffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });

        if (!mounted) return;

        setDocxHtml(result?.value || "");
      } catch (err) {
        console.error("Failed to load DOCX:", err);
        if (mounted) setDocxError("Failed to load document preview.");
      } finally {
        if (mounted) setSourceLoading(false);
      }
    };

    loadDocx();
    return () => { mounted = false; };
  }, [src, documentId, isArchiveFile, filePath]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "auto",
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        background: "#EEF1F5",
        scrollbarWidth: "thin",
        scrollbarColor: "#9AA7B8 #DDE3EC",
        "&::-webkit-scrollbar": { width: 10, height: 10 },
        "&::-webkit-scrollbar-track": { background: "#DDE3EC", borderRadius: "10px" },
        "&::-webkit-scrollbar-thumb": { background: "#9AA7B8", borderRadius: "10px", border: "2px solid #DDE3EC" },
        "&::-webkit-scrollbar-thumb:hover": { background: "#7D8DA3" },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          opacity: sourceLoading ? 1 : 0,
          pointerEvents: sourceLoading ? "auto" : "none",
          transition: "opacity 0.3s ease",
        }}
      >
        <DocumentShimmer
          contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          documentName={documentName}
        />
      </Box>

      {docxError ? (
        <Alert severity="error" sx={{ m: 2 }}>{docxError}</Alert>
      ) : (
        <Box
          sx={{
            minHeight: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 2.5,
            px: 2,
          }}
        >
          <Box
            sx={{
              zoom: docxZoom,
              transformOrigin: "top center",
              width: "min(860px, 100%)",
              background: "#FFFFFF",
              borderRadius: "4px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.16)",
              p: "40px 52px",
              lineHeight: 1.6,
              color: "#111827",
              wordBreak: "break-word",
              "& p": { margin: "0 0 0.9em 0" },
              "& h1, & h2, & h3, & h4, & h5, & h6": { margin: "0.8em 0 0.45em 0", lineHeight: 1.3 },
              "& img": { maxWidth: "100%", height: "auto" },
              "& table": { borderCollapse: "collapse", width: "100%", margin: "0.8em 0" },
              "& th, & td": { border: "1px solid #D0D5DD", padding: "6px 8px" },
            }}
            dangerouslySetInnerHTML={{ __html: docxHtml || "<p>No preview available.</p>" }}
          />
        </Box>
      )}
    </Box>
  );
};

const FilePreviewWithLoader = ({ sasUrl, contentType, documentName, filePath, zoom, isArchiveFile, documentId = null, captionPath = null, captionLang = 'en' }) => {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(true);
  const [audioLoading, setAudioLoading] = useState(true);
  const [imgLoading, setImgLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [captionUrl, setCaptionUrl] = useState(null);
  const videoRef = useRef(null);

  /**
   * Load the caption file and expose it as a blob: URL for the <track> element.
   *
   * A <track> is CORS-restricted in a way <source> is not: pointing it straight at the Azure
   * SAS URL would need the media element to carry crossOrigin="anonymous", which then applies
   * to the video itself and breaks playback unless the storage account's CORS rules are also
   * right. Fetching the .vtt through the existing authenticated proxy and wrapping it in a
   * blob: URL sidesteps that entirely — a blob URL is same-origin by definition, so captions
   * cannot take the video down with them.
   *
   * The blob is typed 'text/vtt' here rather than relying on the proxy's content-type, which
   * has no vtt entry in its MIME map and would hand back application/octet-stream — a type the
   * browser refuses to parse as captions. That keeps this a portal-only change.
   */
  useEffect(() => {
    if (!captionPath) {
      setCaptionUrl(null);
      return undefined;
    }

    let objectUrl = null;
    let isCancelled = false;

    (async () => {
      try {
        const response = await downloadArchiveFileProxy(captionPath);
        const payload = response?.data ?? response;
        if (isCancelled || !payload) return;
        objectUrl = URL.createObjectURL(new Blob([payload], { type: 'text/vtt' }));
        setCaptionUrl(objectUrl);
      } catch (_err) {
        // No captions is a normal state, not a failure worth surfacing — the video still plays.
        if (!isCancelled) setCaptionUrl(null);
      }
    })();

    return () => {
      isCancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [captionPath]);

  const ext = (documentName || filePath || sasUrl || "")
    .split("?")[0]
    .split(".")
    .pop()
    .toLowerCase();
  const detectedType = resolveType(contentType, documentName, sasUrl, filePath);
  const effectiveType = VIDEO_EXTENSIONS.has(ext)
    ? (MIME_MAP[ext] ?? detectedType)   // force correct video MIME from extension
    : detectedType;
  const supportsPdfProxyPreview = detectedType === "application/pdf" && !!documentId;

  useEffect(() => {
    if (effectiveType?.startsWith("video/")) {
      setVideoLoading(true);
    }
  }, [effectiveType, sasUrl]);

  useEffect(() => {
    if (!(effectiveType?.startsWith("video/") && NATIVE_VIDEO_TYPES.has(effectiveType) && sasUrl)) {
      return undefined;
    }

    const videoElement = videoRef.current;
    if (!videoElement) {
      return undefined;
    }

    let isCancelled = false;

    /**
     * Start playback with sound, falling back to muted only if the browser refuses.
     *
     * The modal always opens from a click, which normally satisfies the browser's autoplay
     * policy and lets audio through. It is not guaranteed — Safari is stricter, and a caller
     * that opens the modal without a direct user gesture will be rejected outright — and a
     * rejected play() promise leaves the video sitting there doing nothing at all. So on
     * rejection we mute and try once more: silent playback is worse than sound, but far
     * better than a video that never starts.
     */
    const attemptPlayback = () => {
      if (isCancelled) return;

      const playPromise = videoElement.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          if (isCancelled) return;
          videoElement.muted = true;
          const mutedPlayPromise = videoElement.play();
          if (mutedPlayPromise?.catch) {
            mutedPlayPromise.catch(() => {
              if (!isCancelled) {
                setVideoLoading(false);
              }
            });
          }
        });
      }
    };

    if (videoElement.readyState >= 2) {
      attemptPlayback();
      return () => {
        isCancelled = true;
      };
    }

    videoElement.addEventListener("loadeddata", attemptPlayback);
    videoElement.addEventListener("canplay", attemptPlayback);

    return () => {
      isCancelled = true;
      videoElement.removeEventListener("loadeddata", attemptPlayback);
      videoElement.removeEventListener("canplay", attemptPlayback);
    };
  }, [effectiveType, sasUrl]);

  if (!sasUrl && !filePath && !supportsPdfProxyPreview) {
    return <Alert severity="error">Secure file access URL is missing.</Alert>;
  }

  /* ──────────────── HTML ──────────────── */
  if (detectedType === "text/html") {
    const htmlUrl = isArchiveFile && filePath ? serveHtmlWithSas(filePath) : sasUrl;
    return (
      <Box sx={{ position: "relative", width: "100%", height: "100%", overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            opacity: iframeLoading ? 1 : 0,
            pointerEvents: iframeLoading ? "auto" : "none",
            transition: "opacity 0.3s ease",
          }}
        >
          <DocumentShimmer contentType={detectedType} documentName={documentName} />
        </Box>
        <Box sx={{ width: "100%", height: "100%", minHeight: "100%" }}>
          <iframe
            src={htmlUrl}
            title="HTML Preview"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-pointer-lock allow-modals"
            onLoad={() => setIframeLoading(false)}
            onError={() => { setIframeLoading(false); setIframeError(true); }}
            style={{ width: "100%", height: "100%", minHeight: "60vh", border: "none", display: iframeLoading || iframeError ? "none" : "block" }}
          />
        </Box>
        {iframeError && (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load HTML file.
            <Button size="small" variant="outlined" onClick={() => window.open(htmlUrl, "_blank")} sx={{ ml: 2 }}>
              Open in New Tab
            </Button>
          </Alert>
        )}
      </Box>
    );
  }

  /* ──────────────── EXCEL ──────────────── */
  if (
    detectedType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    detectedType === "application/vnd.ms-excel" ||
    detectedType === "text/csv" ||
    ["xlsx", "xls", "csv"].includes(ext)
  ) {
    return (
      <ExcelPreviewWithLoader
        src={sasUrl}
        documentName={documentName}
        zoom={zoom}
        documentId={documentId}
        isArchiveFile={isArchiveFile}
        filePath={filePath}
      />
    );
  }

  /* ──────────────── WORD (DOCX native renderer) ──────────────── */
  if (
    detectedType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    ext === "docx"
  ) {
    return (
      <DocxPreviewWithLoader
        src={sasUrl}
        documentName={documentName}
        zoom={zoom}
        documentId={documentId}
        isArchiveFile={isArchiveFile}
        filePath={filePath}
      />
    );
  }

  /* ──────────────── WORD (DOC fallback via Office iframe) ──────────────── */
  if (
    detectedType === "application/msword" ||
    ext === "doc"
  ) {
    return (
      <ZoomedIframe
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(sasUrl)}`}
        title="Word Preview"
        contentType={detectedType}
        documentName={documentName}
        zoom={zoom}
        loading={iframeLoading}
        onLoad={() => setIframeLoading(false)}
      />
    );
  }

  /* ──────────────── POWERPOINT ──────────────── */
  if (
    detectedType === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    detectedType === "application/vnd.ms-powerpoint" ||
    ["pptx", "ppt"].includes(ext)
  ) {
    return (
      <ZoomedIframe
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(sasUrl)}`}
        title="PowerPoint Preview"
        contentType={detectedType}
        documentName={documentName}
        zoom={zoom}
        loading={iframeLoading}
        onLoad={() => setIframeLoading(false)}
      />
    );
  }

  /* ──────────────── PDF ──────────────── */
  if (detectedType === "application/pdf") {
    return (
      <PdfPreviewWithLoader
        src={sasUrl}
        documentName={documentName}
        zoom={zoom}
        documentId={documentId}
        isArchiveFile={isArchiveFile}
        filePath={filePath}
      />
    );
  }

  /* ──────────────── IMAGE ──────────────── */
  if (detectedType?.startsWith("image/")) {
    return (
      <Box
        sx={{
          textAlign: "center",
          position: "relative",
          height: "100%",
          overflow: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          p: 2,
          background: "#F7F9FC",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        {imgLoading && <DocumentShimmer contentType={detectedType} documentName={documentName} />}
        <Box
          sx={{
            display: imgLoading ? "none" : "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: "top center",
              width: zoom > 100 ? `${zoom}%` : "100%",
              height: zoom > 100 ? `${zoom}%` : "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img
              src={sasUrl}
              alt={documentName || "Preview"}
              onLoad={() => setImgLoading(false)}
              onError={() => setImgLoading(false)}
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: 8,
                display: "block",
                boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                cursor: zoom > 100 ? "move" : "default",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  if (effectiveType?.startsWith("video/") && NATIVE_VIDEO_TYPES.has(effectiveType)) {
    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          minHeight: "400px",
          overflow: "hidden",      // ← keep this, controls stay inside video box
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          background: "#111318",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            opacity: videoLoading ? 1 : 0,
            pointerEvents: videoLoading ? "auto" : "none",
            transition: "opacity 0.3s ease",
          }}
        >
          <DocumentShimmer contentType={detectedType} documentName={documentName} />
        </Box>
        <Box onContextMenu={(e) => e.preventDefault()} sx={{ width: '100%', height: '100%' }}>
          <video
            ref={videoRef}
            autoPlay
            controls
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            playsInline
            preload="metadata"
            onLoadedData={() => requestAnimationFrame(() => setVideoLoading(false))}
            onCanPlay={() => requestAnimationFrame(() => setVideoLoading(false))}
            onError={() => setVideoLoading(false)}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",   // ← letterboxes video, keeps controls inside
              display: "block",
              visibility: videoLoading ? "hidden" : "visible",
              opacity: videoLoading ? 0 : 1,
              transition: "opacity 0.3s ease",
              background: "#111318",
            }}
          >
            <source src={sasUrl} type={effectiveType} />
            {captionUrl && (
              <track
                kind="captions"
                src={captionUrl}
                srcLang={captionLang}
                label={CAPTION_LANGUAGE_LABELS[captionLang] || captionLang.toUpperCase()}
                default
              />
            )}
            Your browser does not support HTML5 video.
          </video>
        </Box>
      </Box>
    );
  }

  /* ──────────────── VIDEO — non-native (mov, avi, mkv, wmv, flv…) ──────────────── */
  if (effectiveType?.startsWith("video/")) {
    return (
      <DownloadOnlyCard
        sasUrl={sasUrl}
        label="Video File"
        icon="🎬"
        ext={ext}
        description={`The .${ext} format cannot be played directly in the browser. Download the file to watch it in a media player like VLC, QuickTime, or Windows Media Player.`}
      />
    );
  }

  /* ──────────────── AUDIO — native browser formats ──────────────── */
  if (detectedType?.startsWith("audio/") && NATIVE_AUDIO_TYPES.has(detectedType)) {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, background: "#F7F9FC", border: "1px solid #e0e0e0", borderRadius: 1, p: 4 }}>
        {audioLoading && <DocumentShimmer contentType={detectedType} documentName={documentName} />}
        <Box
          sx={{
            width: 180,
            height: 180,
            borderRadius: "20px",
            background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 32px rgba(102,126,234,0.35)",
            fontSize: "4rem",
            animation: audioLoading ? "shimmerPulse 2s ease-in-out infinite" : "none",
          }}
        >
          🎵
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1E293B", mb: 0.5 }}>
            {documentName || "Audio File"}
          </Typography>
          <Chip label={`.${ext.toUpperCase()}`} size="small" sx={{ background: "#EDE9FE", color: "#7C3AED", fontWeight: 700, fontSize: "0.7rem" }} />
        </Box>
        <Box sx={{ width: "min(480px, 90%)" }}>
          <audio
            controls
            preload="metadata"
            onLoadedData={() => setAudioLoading(false)}
            onError={() => setAudioLoading(false)}
            style={{
              width: "100%",
              display: audioLoading ? "none" : "block",
              borderRadius: 8,
            }}
          >
            <source src={sasUrl} type={detectedType} />
            Your browser does not support audio playback.
          </audio>
        </Box>
      </Box>
    );
  }

  /* ──────────────── AUDIO — non-native (flac in some browsers, etc.) ──────────────── */
  if (detectedType?.startsWith("audio/")) {
    return (
      <DownloadOnlyCard
        sasUrl={sasUrl}
        label="Audio File"
        icon="🎵"
        ext={ext}
        description={`The .${ext} format cannot be played directly in the browser. Download the file to listen in your preferred audio player.`}
      />
    );
  }

  /* ──────────────── PLAIN TEXT ──────────────── */
  if (detectedType === "text/plain" || ["txt", "log", "ini", "cfg", "yaml", "yml", "env"].includes(ext)) {
    return (
      <Box sx={{ height: "100%", overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}>
        {iframeLoading && <DocumentShimmer contentType="text/plain" documentName={documentName} />}
        <Box sx={{ transform: `scale(${zoom / 100})`, transformOrigin: "top left", width: `${(100 / zoom) * 100}%`, height: `${(100 / zoom) * 100}%`, minHeight: "100%" }}>
          <iframe
            src={sasUrl}
            title="Text Preview"
            onLoad={() => setIframeLoading(false)}
            style={{ width: "100%", height: "100%", minHeight: "60vh", border: "none", backgroundColor: "#FAFAFA", display: iframeLoading ? "none" : "block" }}
          />
        </Box>
      </Box>
    );
  }

  /* ──────────────── JSON / XML / MARKDOWN — render as text ──────────────── */
  if (["application/json", "application/xml", "text/markdown", "text/csv"].includes(detectedType) ||
    ["json", "xml", "md", "markdown", "csv"].includes(ext)) {
    return (
      <Box sx={{ height: "100%", overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}>
        {iframeLoading && <DocumentShimmer contentType={detectedType} documentName={documentName} />}
        <Box sx={{ transform: `scale(${zoom / 100})`, transformOrigin: "top left", width: `${(100 / zoom) * 100}%`, height: `${(100 / zoom) * 100}%`, minHeight: "100%" }}>
          <iframe
            src={sasUrl}
            title={`${ext?.toUpperCase() || "Code"} Preview`}
            onLoad={() => setIframeLoading(false)}
            style={{ width: "100%", height: "100%", minHeight: "60vh", border: "none", backgroundColor: "#FAFAFA", fontFamily: "monospace", display: iframeLoading ? "none" : "block" }}
          />
        </Box>
      </Box>
    );
  }

  /* ──────────────── ARCHIVES ──────────────── */
  if (ARCHIVE_TYPES.has(detectedType) || ["zip", "rar", "tar", "gz", "7z"].includes(ext)) {
    return (
      <DownloadOnlyCard
        sasUrl={sasUrl}
        label="Archive File"
        icon="📦"
        ext={ext}
        description="Archive files cannot be previewed in the browser. Download to extract and view the contents."
      />
    );
  }

  /* ──────────────── BINARY / UNKNOWN ──────────────── */
  if (BINARY_TYPES.has(detectedType) || ["exe", "bin", "iso", "dmg", "msi", "deb", "rpm"].includes(ext)) {
    return (
      <DownloadOnlyCard
        sasUrl={sasUrl}
        label="Binary File"
        icon="💾"
        ext={ext}
        description="This file type cannot be previewed in the browser. Download the file to open it with the appropriate application."
      />
    );
  }

  /* ──────────────── FINAL FALLBACK ──────────────── */
  return (
    <DownloadOnlyCard
      sasUrl={sasUrl}
      label="File Preview Unavailable"
      icon="📄"
      ext={ext}
      description={`Preview is not supported for this file type${detectedType ? ` (${detectedType})` : ""}. You can download the file to open it locally.`}
    />
  );
};

/* ------------------------------------------------------------------ */
/* Build breadcrumbs for archive files                                */
/* ------------------------------------------------------------------ */
const buildBreadcrumbs = (fullPath, rootPath) => {
  if (!fullPath || typeof fullPath !== "string") return [];

  let detectedRoot = rootPath;
  if (!detectedRoot) {
    if (fullPath.startsWith("historical-content/")) {
      detectedRoot = "historical-content";
    } else if (fullPath.startsWith("Training Materials/")) {
      detectedRoot = "Training Materials";
    }
  }

  const pathWithoutRoot = detectedRoot
    ? fullPath.replace(new RegExp(`^${detectedRoot}/`), "")
    : fullPath;

  const segments = pathWithoutRoot.split("/").filter(Boolean);

  if (segments.length > 0 && segments[segments.length - 1].toLowerCase() === 'index.html') {
    return segments.slice(0, -1);
  }

  return segments;
};

/* ------------------------------------------------------------------ */
/* FIX 1: Helper — truncate breadcrumbs at B-CBT for CBT HTML files  */
/* Only used when the file being displayed is a CBT HTML entry.       */
/* ------------------------------------------------------------------ */
const truncateBreadcrumbsAtCBT = (segments) => {
  const cbtIdx = segments.findIndex((seg) => {
    const normalized = seg.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
    return normalized === 'bcbt' || normalized === 'cbt';
  });
  // If a B-CBT segment is found, keep everything up to and including it
  return cbtIdx >= 0 ? segments.slice(0, cbtIdx + 1) : segments;
};

/* ------------------------------------------------------------------ */
/* Utility helpers                                                     */
/* ------------------------------------------------------------------ */
const escapeRegExp = (value = "") => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/* ------------------------------------------------------------------ */
/* Main Component                                                      */
/* ------------------------------------------------------------------ */

const DocumentViewModal = ({
  open,
  setOpen,
  document: documentData,
  filePath = null,
  fileName = null,
  displayName = null,
  archiveRootPath = null,
  onClose,
  onEdit,
  onDelete,
  width = "60vw",
  useDialogLayout = false,
  showBreadcrumbs = true,
  showReloadButton = true,
  showDownloadButton = true,
  captionPath = null,
  captionLang = 'en',
}) => {
  const { t } = useTranslation();

  const [sasUrl, setSasUrl] = useState(null);
  const [sasExpiry, setSasExpiry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [useFallback, setUseFallback] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [zoom, setZoom] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  /* FIX 2: ref for the dialog content box so we can focus it and     */
  /* intercept keyboard events even when an iframe has page focus.    */
  const dialogContentRef = useRef(null);

  const handleClose = () => {
    onClose?.();
    setOpen?.(false);
  };

  const isArchiveFile = !!filePath && !documentData?.id;
  const isHtmlFile = [
    fileName,
    documentData?.name,
    documentData?.blob_path,
    documentData?.attachment,
  ].some((value) => String(value || '').split('?')[0].toLowerCase().endsWith('.html'));
  const isVideoFile = useMemo(() => {
    const name = documentData?.name || fileName || '';
    const ext = name.split('.').pop()?.toLowerCase(); // ← toLowerCase() fixes MP4/MOV
    const type = documentData?.content_type || '';
    return (
      type.startsWith('video/') ||
      VIDEO_EXTENSIONS.has(ext) // ← use the same Set
    );
  }, [documentData?.name, documentData?.content_type, fileName]);
  const isPdfDocument = useMemo(() => {
    const name = (documentData?.name || fileName || '').toLowerCase();
    return documentData?.content_type === "application/pdf" || name.endsWith(".pdf");
  }, [documentData?.content_type, documentData?.name, fileName]);
  const isDownloadOnlyPreview = useMemo(() => {
    const previewName = documentData?.name || fileName || filePath || "";
    const previewExt = previewName.split("?")[0].split(".").pop()?.toLowerCase();
    const previewType = resolveType(
      documentData?.content_type || null,
      previewName,
      sasUrl || "",
      filePath || null
    );

    return (
      ARCHIVE_TYPES.has(previewType) ||
      BINARY_TYPES.has(previewType) ||
      ["zip", "rar", "tar", "gz", "7z", "exe", "bin", "iso", "dmg", "msi", "deb", "rpm"].includes(previewExt)
    );
  }, [documentData?.content_type, documentData?.name, fileName, filePath, sasUrl]);
  const canUseZoom = !isVideoFile && !isHtmlFile && !isDownloadOnlyPreview;

  const titleLabel = String(
    displayName || documentData?.name || fileName || t("TABLE_HEADERS.DOCUMENT_NAME") || "Document"
  ).replace(/\.html$/i, "");
  const shouldShowBreadcrumbs = showBreadcrumbs && isArchiveFile && !!filePath;
  const breadcrumbTitle =
    shouldShowBreadcrumbs && archiveRootPath && filePath
      ? filePath.replace(new RegExp(`^${escapeRegExp(archiveRootPath)}/`), "")
      : filePath;

  const attachment = useMemo(
    () => normalizeAttachment(documentData),
    [documentData]
  );

  const assignedMachines = useMemo(() => {
    if (!documentData) return [];
    if (Array.isArray(documentData.machine_details) && documentData.machine_details.length > 0) {
      return documentData.machine_details;
    }
    if (Array.isArray(documentData.assigned_machines)) {
      return documentData.assigned_machines;
    }
    return [];
  }, [documentData]);

  const categories = useMemo(() => {
    if (!documentData) return [];
    return Array.isArray(documentData.categories) ? documentData.categories : [];
  }, [documentData]);

  const primaryCategoryLabel = useMemo(() => {
    const categoryValue =
      categories[0]?.category_name ||
      categories[0]?.label ||
      documentData?.category ||
      "";

    if (!categoryValue) return "";
    return getModalCategoryLabel(t, normalizeDocumentCategoryKey(categoryValue));
  }, [categories, documentData?.category, t]);

  const isAzureDocument = !!attachment?.value && !isArchiveFile;
  const directPreviewUrl =
    documentData?.sas_url ||
    documentData?.url ||
    (documentData?.attachment_type === "url" ? documentData?.attachment : null);

  const isExportableHtmlManual = isHtmlFile && isAzureDocument;

  const handleExportHtmlPdf = async () => {
    const blobPath = documentData?.blob_path || documentData?.attachment || attachment?.value;
    if (!blobPath || exportingPdf) return;

    setExportingPdf(true);
    setSnackbarState({ open: true, message: "Preparing manual PDF...", severity: "info" });
    try {
      const pdfBlob = await exportHtmlManualPdf(blobPath);
      if (!(pdfBlob instanceof Blob) || pdfBlob.size === 0) {
        throw new Error(pdfBlob?.error || pdfBlob?.message || "PDF export returned no file");
      }
      if (pdfBlob.type !== "application/pdf") {
        const responseText = await pdfBlob.text();
        let message = "Failed to export manual PDF";
        try {
          message = JSON.parse(responseText)?.error || message;
        } catch (_) {
          if (responseText) message = responseText;
        }
        throw new Error(message);
      }

      const downloadUrl = URL.createObjectURL(pdfBlob);
      const link = window.document.createElement("a");
      link.href = downloadUrl;
      link.download = `${titleLabel.replace(/\.html$/i, "") || "Manual"}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
      setSnackbarState({ open: true, message: "Manual PDF exported", severity: "success" });
    } catch (err) {
      setSnackbarState({
        open: true,
        message: err?.message || "Failed to export manual PDF",
        severity: "error",
      });
    } finally {
      setExportingPdf(false);
    }
  };

  useEffect(() => {
    if (!open || (!documentData?.id && !filePath)) {
      setSasUrl(null);
      setSasExpiry(null);
      setError(null);
      setLoading(false);
      setUseFallback(false);
      setZoom(100);
      setIsFullscreen(false);
    }
  }, [open, isHtmlFile, documentData?.id, filePath]);

  /* FIX 2: Auto-focus the content box when the dialog opens so that  */
  /* keyboard shortcuts work immediately without requiring a click.   */
  useEffect(() => {
    if (open) {
      // Small delay to let the Dialog finish its open animation
      const t = setTimeout(() => {
        dialogContentRef.current?.focus();
      }, 150);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (isArchiveFile && filePath) {
      if (isHtmlFile) {
        setLoading(false);
        return;
      }

      const fetchArchiveFile = async () => {
        setLoading(true);
        setError(null);

        try {
          const response = await getArchiveFileAccess(filePath);

          if (!response?.data?.sasUrl && !response?.sasUrl) {
            throw new Error(`Failed to get file access`);
          }

          const sasUrlValue = response.data?.sasUrl || response.sasUrl;
          setSasUrl(sasUrlValue);
        } catch (err) {
          setError(err.message || "Failed to load file");
        } finally {
          setLoading(false);
        }
      };

      fetchArchiveFile();
      return;
    }

    if (documentData?.id && isAzureDocument) {
      if (isHtmlFile) {
        const htmlBlobPath = documentData?.blob_path || documentData?.attachment;
        if (htmlBlobPath) {
          setSasUrl(serveHtmlWithSas(htmlBlobPath));
          setSasExpiry(null);
          setLoading(false);
          setError(null);
          setUseFallback(false);
          return;
        }
      }

      if (isPdfDocument) {
        setSasUrl(null);
        setSasExpiry(null);
        setLoading(false);
        setError(null);
        setUseFallback(false);
        return;
      }

      const fetchSasUrl = async () => {
        try {
          setLoading(true);
          setError(null);
          setUseFallback(false);

          if (documentData.sas_url && documentData.sas_expires_at) {
            const expiry = new Date(documentData.sas_expires_at).getTime();
            if (expiry - Date.now() > 5 * 60 * 1000) {
              setSasUrl(documentData.sas_url);
              setSasExpiry(documentData.sas_expires_at);
              setLoading(false);
              return;
            }
          }

          const documentId = documentData.id;
          const response = await getDocumentSasUrl(documentId);

          if (!response?.data?.sas_url && !response?.data?.url) {
            setUseFallback(true);
            setLoading(false);
            return;
          }

          if (response.data.storage_type === 'local') {
            setSasUrl(response.data.url || response.data.download_url);
            setSasExpiry(null);
          } else if (response.data.sas_url) {
            setSasUrl(response.data.sas_url);
            setSasExpiry(response.data.expires_at);
          } else if (response.data.url) {
            setSasUrl(response.data.url);
            setSasExpiry(null);
          } else {
            setUseFallback(true);
          }
        } catch (err) {
          setUseFallback(true);
        } finally {
          setLoading(false);
        }
      };

      fetchSasUrl();
    }

    if (!documentData?.id && directPreviewUrl && isPdfDocument) {
      setSasUrl(directPreviewUrl);
      setSasExpiry(null);
      setLoading(false);
      setError(null);
      setUseFallback(false);
    }
  }, [open, documentData?.id, documentData?.blob_path, documentData?.sas_url, documentData?.sas_expires_at, documentData?.url, documentData?.attachment, documentData?.attachment_type, directPreviewUrl, isAzureDocument, isArchiveFile, filePath, isHtmlFile, isPdfDocument]);

  const handleZoomIn = useCallback(() => {
    if (!canUseZoom) return;
    setZoom(prev => Math.min(prev + 10, 200));
  }, [canUseZoom]);
  const handleZoomOut = useCallback(() => {
    if (!canUseZoom) return;
    setZoom(prev => Math.max(prev - 10, 50));
  }, [canUseZoom]);
  const handleResetZoom = useCallback(() => {
    if (!canUseZoom) return;
    setZoom(100);
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1'
      );
    }
    viewportManipulated.current = false;
  }, [canUseZoom]);
  const toggleFullscreen = useCallback(() => setIsFullscreen(prev => !prev), []);

  /* FIX 2: Shared keyboard handler used by BOTH the window listener  */
  /* AND the onKeyDown prop on the content box. This ensures zoom      */
  /* shortcuts work even when an iframe has stolen window focus.      */
  const handleKeyDownZoom = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=')) {
      e.preventDefault();
      handleZoomIn();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === '-') {
      e.preventDefault();
      handleZoomOut();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === '0') {
      e.preventDefault();
      handleResetZoom();
    }
  }, [handleZoomIn, handleZoomOut, handleResetZoom]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('keydown', handleKeyDownZoom);
    return () => window.removeEventListener('keydown', handleKeyDownZoom);
  }, [open, handleKeyDownZoom]);

  const viewportManipulated = useRef(false);

  useEffect(() => {
    if (!open) return;

    // Disable browser zoom immediately when modal opens
    const disableBrowserZoom = () => {
      if (!viewportManipulated.current) {
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute(
            'content',
            'width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no'
          );
          viewportManipulated.current = true;
        }
      }
    };

    // Disable browser zoom right away
    disableBrowserZoom();

    const handleWheel = (e) => {
      // Only intercept pinch-to-zoom (trackpad = ctrlKey + wheel)
      if (!e.ctrlKey && !e.metaKey) return;

      e.preventDefault();

      // Ensure browser zoom is disabled (in case it wasn't already)
      disableBrowserZoom();
      if (!canUseZoom) return;

      if (e.deltaY < 0) {
        setZoom(prev => Math.min(prev + 10, 200));
      } else if (e.deltaY > 0) {
        setZoom(prev => Math.max(prev - 10, 50));
      }
    };

    // Also handle keyboard zoom (Ctrl/Cmd + +/-)
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '0')) {
        e.preventDefault();
        disableBrowserZoom();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('keydown', handleKeyDown);

      // Restore viewport when modal closes
      if (viewportManipulated.current) {
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute(
            'content',
            'width=device-width, initial-scale=1'
          );
        }
        viewportManipulated.current = false;
      }
    };
  }, [open, canUseZoom]);

  // AFTER — unified: both paths use setSnackbarState
  const refreshSasUrl = async () => {
    if (isArchiveFile && filePath) {
      setSasUrl(null);
      setLoading(true);
      setError(null);

      try {
        const response = await getArchiveFileAccess(filePath);
        if (!response?.data?.sasUrl && !response?.sasUrl) throw new Error("Refresh failed");
        setSasUrl(response.data?.sasUrl || response.sasUrl);
        setSnackbarState({ open: true, message: "Document reloaded successfully", severity: "success" });
      } catch (err) {
        setError("Failed to refresh file access");
        setSnackbarState({ open: true, message: "Failed to reload document", severity: "error" });
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      setUseFallback(false);

      const response = await getDocumentSasUrl(documentData.id, 24);

      if (!response?.data?.sas_url && !response?.data?.url) {
        setUseFallback(true);
        throw new Error("Refresh failed");
      }

      if (response.data.storage_type === 'local') {
        setSasUrl(response.data.url || response.data.download_url);
        setSasExpiry(null);
      } else if (response.data.sas_url) {
        setSasUrl(response.data.sas_url);
        setSasExpiry(response.data.expires_at);
      } else {
        setUseFallback(true);
      }

      // Same Snackbar as archive path — no more Swal popup
      setSnackbarState({ open: true, message: "Document reloaded successfully", severity: "success" });
    } catch (error) {
      setSnackbarState({ open: true, message: "Failed to reload document", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = useCallback((message, severity = "info") => {
    setSnackbarState({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  const handleDownload = async () => {
    // Handle archive files (historical documentation)
    if (isArchiveFile && filePath && fileName) {
      if (isHtmlFile) {
        setSnackbarState({ open: true, message: "HTML files cannot be downloaded directly", severity: "warning" });
        return;
      }

      try {
        setSnackbarState({ open: true, message: "Starting download...", severity: "info" });

        // Route through backend proxy — avoids CORS entirely
        const blob = await downloadArchiveFileProxy(filePath);

        if (!blob || blob.size < 100) {
          throw new Error("Received invalid file data.");
        }

        if (blob.type === 'application/json') {
          const text = await blob.text();
          const json = JSON.parse(text);
          throw new Error(json.message || "Download failed on server.");
        }

        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);

        setSnackbarState({ open: true, message: "Download successful", severity: "success" });

      } catch (err) {
        console.error("Download error:", err);
        setSnackbarState({
          open: true,
          message: err.message || "Failed to download file. Please try again.",
          severity: "error"
        });
      }
      return;  // ← exits after archive handling
    }

    // ← This block was OUTSIDE the function in your file — now it's inside
    // Handle regular documents (with documentData)
    if (!documentData) return;

    try {
      const filename = documentData.name || 'document';

      if (documentData.id && (documentData.blob_path || (documentData.attachment_type === 'file' && documentData.attachment))) {
        setSnackbarState({ open: true, message: "Starting download...", severity: "info" });

        const blob = await downloadDocumentProxy(documentData.id);

        if (!blob || blob.size < 1) {
          throw new Error("Received empty file from server.");
        }

        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);

        showSnackbar(t('MESSAGES.DOWNLOAD_SUCCESS') || 'Download successful', 'success');
        return;
      }

      if (documentData.attachment_type === 'url' && documentData.attachment) {
        window.open(documentData.attachment, '_blank');
        return;
      }

      const downloadUrl = documentData.url || documentData.attachment;
      if (downloadUrl) {
        const response = await fetch(downloadUrl);
        if (!response.ok) throw new Error("Unable to download document.");
        const blobUrl = window.URL.createObjectURL(await response.blob());
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
        showSnackbar(t('MESSAGES.DOWNLOAD_SUCCESS') || 'Download successful', 'success');
        return;
      }

      showSnackbar(t('MESSAGES.NO_ATTACHMENT') || 'No attachment available', 'warning');

    } catch (error) {
      console.error("Download error:", error);
      showSnackbar(t('MESSAGES.DOWNLOAD_ERROR') || 'Download failed', 'error');
    }
  };

  /* ------------------------------------------------------------------ */
  /* Render Preview Section                                             */
  /* ------------------------------------------------------------------ */

  const renderPreview = () => {
    if (loading && !isPdfDocument) {
      return (
        <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
          <DocumentShimmer
            contentType={documentData?.content_type || null}
            documentName={documentData?.name || fileName}
          />
        </Box>
      );
    }

    if (isArchiveFile) {
      if (sasUrl || isHtmlFile || isPdfDocument) {
        return (
          <FilePreviewWithLoader
            sasUrl={sasUrl}
            contentType={null}
            documentName={fileName}
            filePath={filePath}
            zoom={zoom}
            isArchiveFile={true}
            documentId={null}
            captionPath={captionPath}
            captionLang={captionLang}
          />
        );
      }
      return <Alert severity="info">No preview available.</Alert>;
    }

    if (attachment.type === "file") {
      if (useFallback) {
        return <Alert severity="info">Local preview not available.</Alert>;
      }

      // Direct Azure report URLs are copied into sasUrl in an effect after the
      // modal opens. Keep the initial render in the loading state instead of
      // briefly showing the secure-access error while that effect completes.
      if (isPdfDocument && directPreviewUrl && !sasUrl) {
        return (
          <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            <DocumentShimmer contentType="application/pdf" documentName={documentData?.name || fileName} />
          </Box>
        );
      }

      if (sasUrl || isPdfDocument) {
        return (
          <FilePreviewWithLoader
            sasUrl={sasUrl}
            contentType={isHtmlFile ? "text/html" : documentData.content_type}
            documentName={documentData.name}
            filePath={null}
            zoom={zoom}
            isArchiveFile={false}
            documentId={documentData?.id || null}
          />
        );
      }

      return (
        <Alert severity="info">
          No preview available.
          <br />
          <Button size="small" variant="outlined" onClick={handleDownload} sx={{ mt: 1 }}>
            Download File
          </Button>
        </Alert>
      );
    }

    if (attachment.type === "url") {
      return (
        <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>External Link</Typography>
          <Typography variant="body1" sx={{ wordBreak: "break-all", color: "primary.main", textDecoration: "underline" }}>
            {attachment.value}
          </Typography>
          <Button size="small" variant="outlined" onClick={() => window.open(attachment.value, "_blank", "noopener,noreferrer")} sx={{ mt: 2 }}>
            Open Link in New Tab
          </Button>
        </Box>
      );
    }

    return <Typography variant="body2" color="text.secondary">No attachment available</Typography>;
  };

  /* ------------------------------------------------------------------ */
  /* Zoom Controls Bar                                                  */
  /* ------------------------------------------------------------------ */

  const ZoomControls = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pt: 1,
        pb: 0.5,
        px: 1,
        flexShrink: 0,
        borderTop: "1px solid",
        borderColor: "divider",
        position: "sticky",
        bottom: 0,
        zIndex: 10,
        backgroundColor: "background.paper",
        boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
        {/* Ctrl/Cmd + +/− to zoom · Ctrl/Cmd + 0 to reset */}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <ButtonGroup
          size="small"
          variant="outlined"
          sx={{
            "& .MuiButton-root": {
              minWidth: 36,
              width: 36,
              height: 36,
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
          }}
        >
          <Tooltip title="Zoom Out (Ctrl/Cmd + -)">
            <span>
              <Button onClick={handleZoomOut} disabled={zoom <= 50}>
                <ZoomOutIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>

          <Button>
            {zoom}%
          </Button>

          <Tooltip title="Zoom In (Ctrl/Cmd + +)">
            <span>
              <Button onClick={handleZoomIn} disabled={zoom >= 200}>
                <ZoomInIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Reset Zoom (Ctrl/Cmd + 0)">
            <Button onClick={handleResetZoom}>
              <RestartAltIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
            <Button onClick={toggleFullscreen}>
              {isFullscreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Box>

    </Box>
  );

  /* ------------------------------------------------------------------ */
  /* Render — Dialog layout                                             */
  /* ------------------------------------------------------------------ */

  if (useDialogLayout || isArchiveFile) {
    /*
     * FIX 1: Build breadcrumb segments, then — only for CBT HTML files —
     * truncate them at the B-CBT folder so we never show
     * "HTML > 140000000_R4TA_000A_EN" in the title bar.
     */
    const rawCrumbs = shouldShowBreadcrumbs
      ? buildBreadcrumbs(filePath, archiveRootPath)
      : [];
      const displayCrumbs = (isHtmlFile
      ? truncateBreadcrumbsAtCBT(rawCrumbs)
      : rawCrumbs
    ).map((crumb) => {
      if (crumb === "B-CBT") return "Basic Computer Based Training";
      if (crumb === "C-VBT") return "Basic Video Based Training";
      if (crumb?.trim().toLowerCase() === "online") return "Basic Computer Based Training";
      return crumb;
    });

    return (
      <>
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth={isFullscreen ? false : "lg"}
          fullWidth
          fullScreen={isFullscreen}
          PaperProps={{
            sx: {
              height: isFullscreen ? "100vh" : "85vh",
              maxHeight: isFullscreen ? "100vh" : "85vh",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <DialogTitle sx={{ px: isFullscreen ? { xs: 2, sm: 3 } : 3, py: isFullscreen ? 1.5 : 2, pr: 7, flexShrink: 0 }}>
            <Box display="flex" flexDirection="column" gap={0.3}>
              {shouldShowBreadcrumbs ? (
                <Box
                  sx={{
                    minWidth: 0,
                    width: "100%",
                    display: "block",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                  title={breadcrumbTitle}
                >
                  <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                    {displayCrumbs.map((segment, index, arr) => {
                      const isLast = index === arr.length - 1;
                      return (
                        <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: isLast ? 600 : 400, color: "primary.main" }}
                          >
                            {segment}
                          </Typography>
                          {!isLast && (
                            <Typography sx={{ mx: 0.5, color: "primary.main" }}>&gt;</Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }} title={titleLabel}>
                  {titleLabel}
                </Typography>
              )}
            </Box>
            {/* Close button — top right, same position as ModalWrapper */}
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          {/*
           * FIX 2: tabIndex={-1} makes the box programmatically focusable.
           * ref={dialogContentRef} lets us call .focus() on open.
           * onKeyDown={handleKeyDownZoom} catches keyboard events even when
           * an iframe inside has stolen window-level focus.
           * outline: 'none' removes the browser's default focus ring.
           */}
          <DialogContent
            ref={dialogContentRef}
            tabIndex={-1}
            onKeyDown={handleKeyDownZoom}
            sx={{ p: 2, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, outline: "none" }}
          >
            <Box display="flex" flexDirection="column" height="100%">
              {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}
              <Box
                flex={1}
                sx={{
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  height: 0,           // ← ADD THIS — forces flex child to respect parent height
                  minHeight: 0,        // ← ADD THIS
                }}
              >
                {renderPreview()}
              </Box>
              {canUseZoom && <ZoomControls />}
            </Box>
          </DialogContent>

          <DialogActions sx={{ flexShrink: 0, justifyContent: 'space-between', px: isFullscreen ? { xs: 2, sm: 3 } : 2, py: 1.5, gap: 1 }}>
            {/* Left side */}
            <Box display="flex" gap={1}>
              <Button onClick={handleClose}>{t("BUTTON.CLOSE") || "Close"}</Button>
            </Box>
            {/* Right side */}
            <Box display="flex" gap={1}>
              {!isHtmlFile && !isPdfDocument && showReloadButton && (
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshSasUrl} disabled={loading || !sasUrl}>
                  {t("UI_TEXT.RELOAD") || "Reload Document"}
                </Button>
              )}
              {(sasUrl || isPdfDocument || isArchiveFile) && !isHtmlFile && showDownloadButton && (
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
                  {t("UI_TEXT.DOWNLOAD") || "Download"}
                </Button>
              )}
              {isExportableHtmlManual && (
                <Button
                  variant="contained"
                  startIcon={exportingPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
                  onClick={handleExportHtmlPdf}
                  disabled={exportingPdf}
                >
                  {exportingPdf ? "Exporting..." : "Export PDF"}
                </Button>
              )}
              {isHtmlFile && (
                <Button
                  variant="outlined"
                  startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  onClick={toggleFullscreen}
                  size="small"
                >
                  {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                </Button>
              )}
            </Box>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarState.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarState.severity} sx={{ width: "100%" }}>
            {snackbarState.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Render — ModalWrapper layout                                       */
  /* ------------------------------------------------------------------ */

  return (
    <>
      <ModalWrapper
        fullHeight={true}
        open={open}
        setOpen={setOpen}
        onClose={handleClose}
        fullScreen={isFullscreen}
        title={
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" flexWrap="wrap" gap={1}>
            <Box display="flex" flexDirection="column" gap={0.3} flex={1} minWidth={0}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.25, overflowWrap: "anywhere" }} title={titleLabel}>
                {titleLabel}
              </Typography>
              {assignedMachines.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", lineHeight: 1.3, pt: 0.25 }}>
                  <strong>Machine No:</strong> {assignedMachines[0].machine_number || "N/A"}
                </Typography>
              )}
            </Box>
            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.4} sx={{ minWidth: 0, maxWidth: { xs: "100%", sm: "48%" } }}>
              {!!categories.length && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 600 }}>
                    Category:
                  </Typography>
                  <Chip
                    label={primaryCategoryLabel}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ height: 20, fontSize: "0.65rem" }}
                  />
                </Box>
              )}
              {assignedMachines.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", lineHeight: 1.3, pt: 0.25, textAlign: "right", overflowWrap: "anywhere" }}>
                  <strong>Machine Name:</strong> {assignedMachines[0].machine_name || "Unknown"}
                </Typography>
              )}
            </Box>
          </Box>
        }
        width={isFullscreen ? "100vw" : width}
        content={
          /*
           * FIX 2: Same keyboard-capture trick for the ModalWrapper layout.
           * tabIndex + onKeyDown ensures zoom shortcuts work here too.
           */
          <Box
            ref={dialogContentRef}
            tabIndex={-1}
            onKeyDown={handleKeyDownZoom}
            display="flex"
            flexDirection="column"
            height="100%"
            sx={{ outline: "none" }}
          >
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box
              flex={1}
              sx={{
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: 0,           // ← ADD THIS
                minHeight: 0,        // ← ADD THIS
              }}
            >
              {renderPreview()}
            </Box>
            {canUseZoom && <ZoomControls />}
          </Box>
        }
        actions={
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box display="flex" gap={1}>
              <Button onClick={handleClose}>{t("BUTTON.CLOSE") || "Close"}</Button>
              {onDelete && documentData && (
                <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(documentData)}>
                  {t("BUTTON.DELETE") || "Delete"}
                </Button>
              )}
            </Box>
            <Box display="flex" gap={1}>
              {isExportableHtmlManual && (
                <Button
                  variant="contained"
                  startIcon={exportingPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
                  onClick={handleExportHtmlPdf}
                  disabled={exportingPdf}
                >
                  {exportingPdf ? "Exporting..." : "Export PDF"}
                </Button>
              )}
              {isHtmlFile && (
                <Button
                  variant="outlined"
                  startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  onClick={toggleFullscreen}
                  size="small"
                >
                  {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                </Button>
              )}
              {isAzureDocument && !useFallback && !isHtmlFile && !isPdfDocument && showReloadButton && (
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshSasUrl} disabled={loading || !sasUrl}>
                  {t("UI_TEXT.RELOAD") || "Reload Document"}
                </Button>
              )}
              {!isHtmlFile && (attachment.type === "file" || isArchiveFile) && showDownloadButton && (
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
                  {t("UI_TEXT.DOWNLOAD") || "Download"}
                </Button>
              )}
              {onEdit && documentData && (
                <Button variant="contained" startIcon={<EditIcon />} onClick={() => onEdit(documentData)}>
                  {t("BUTTON.EDIT") || "Edit"}
                </Button>
              )}
            </Box>
          </Box>
        }
      />

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarState.severity} sx={{ width: "100%" }}>
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DocumentViewModal; 
